import { Component, ComponentType, RefreshBadgeConfig } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";
import { useEditorStore } from "@/stores/editor/editor-store";
import { styleCloneService, StyleCloneScope } from "../../StyleCloneService";
import { componentReferenceResolver } from "../../ComponentReferenceResolver";
import { getComponentDescendants } from "@/utils/componentHierarchy";
import { BRAND_COLORS } from "../constants/brand-colors";
import {
  FAINT_COLORS,
  RADIUS_PRESETS,
  BORDER_WIDTHS,
  DEFAULT_BORDER_COLOR,
  SHADOW_PRESETS,
  INSET_SHADOW_PRESETS,
  ELEVATION_MAP,
  STYLE_PRESETS,
} from "../constants/flex-container-constants";

/** Style shape for typed access to flex container style properties */
interface FlexContainerStyleProps extends Record<string, unknown> {
  borderColor?: string;
  borderStyle?: string;
  borderWidth?: string | number;
  borderRadius?: string | number;
  borderTopLeftRadius?: string;
  borderTopRightRadius?: string;
  borderBottomLeftRadius?: string;
  borderBottomRightRadius?: string;
  padding?: string | number;
  paddingTop?: string | number;
  paddingBottom?: string | number;
  gap?: string | number;
  margin?: string | number;
  fontSize?: string | number;
  fontWeight?: string | number;
  letterSpacing?: string | number;
  lineHeight?: string | number;
  background?: string;
  backgroundColor?: string;
  boxShadow?: string;
  opacity?: number;
}

/** Properties shape for typed access to flex container properties */
interface FlexContainerProps extends Record<string, unknown> {
  borderRadius?: number;
  padding?: string | number;
  gap?: string | number;
  margin?: string | number;
  fontSize?: string | number;
  fontWeight?: string | number;
  letterSpacing?: string | number;
  lineHeight?: string | number;
  backgroundColor?: string;
  minHeight?: string;
  flexDirection?: string;
  justifyContent?: string;
  alignItems?: string;
  content?: string;
  dataSource?: { type?: string };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasOptionalString(value: Record<string, unknown>, key: string): boolean {
  const field = value[key];
  return field === undefined || typeof field === 'string';
}

function hasOptionalStringOrNumber(value: Record<string, unknown>, key: string): boolean {
  const field = value[key];
  return field === undefined || typeof field === 'string' || typeof field === 'number';
}

function isFlexContainerStyleProps(value: Record<string, unknown>): value is FlexContainerStyleProps {
  return (
    hasOptionalString(value, 'borderColor') &&
    hasOptionalString(value, 'borderStyle') &&
    hasOptionalStringOrNumber(value, 'borderWidth') &&
    hasOptionalStringOrNumber(value, 'borderRadius') &&
    hasOptionalString(value, 'borderTopLeftRadius') &&
    hasOptionalString(value, 'borderTopRightRadius') &&
    hasOptionalString(value, 'borderBottomLeftRadius') &&
    hasOptionalString(value, 'borderBottomRightRadius') &&
    hasOptionalStringOrNumber(value, 'padding') &&
    hasOptionalStringOrNumber(value, 'paddingTop') &&
    hasOptionalStringOrNumber(value, 'paddingBottom') &&
    hasOptionalStringOrNumber(value, 'gap') &&
    hasOptionalStringOrNumber(value, 'margin') &&
    hasOptionalStringOrNumber(value, 'fontSize') &&
    hasOptionalStringOrNumber(value, 'fontWeight') &&
    hasOptionalStringOrNumber(value, 'letterSpacing') &&
    hasOptionalStringOrNumber(value, 'lineHeight') &&
    hasOptionalString(value, 'background') &&
    hasOptionalString(value, 'backgroundColor') &&
    hasOptionalString(value, 'boxShadow') &&
    (value.opacity === undefined || typeof value.opacity === 'number')
  );
}

function isFlexContainerProps(value: Record<string, unknown>): value is FlexContainerProps {
  const dataSource = value.dataSource;
  return (
    (value.borderRadius === undefined || typeof value.borderRadius === 'number') &&
    hasOptionalStringOrNumber(value, 'padding') &&
    hasOptionalStringOrNumber(value, 'gap') &&
    hasOptionalStringOrNumber(value, 'margin') &&
    hasOptionalStringOrNumber(value, 'fontSize') &&
    hasOptionalStringOrNumber(value, 'fontWeight') &&
    hasOptionalStringOrNumber(value, 'letterSpacing') &&
    hasOptionalStringOrNumber(value, 'lineHeight') &&
    hasOptionalString(value, 'backgroundColor') &&
    hasOptionalString(value, 'minHeight') &&
    hasOptionalString(value, 'flexDirection') &&
    hasOptionalString(value, 'justifyContent') &&
    hasOptionalString(value, 'alignItems') &&
    hasOptionalString(value, 'content') &&
    (dataSource === undefined || (isRecord(dataSource) && hasOptionalString(dataSource, 'type')))
  );
}

/**
 * Command handler for flex-container components
 * Handles layout, sizing, and flexbox-specific commands
 *
 * Phase 1 Enhancements (December 2025):
 * - Full color palette support (named, hex, RGB/RGBA)
 * - TerraMatrix brand color integration
 * - Gradient support (linear and radial)
 * - Opacity control
 */
export class FlexContainerCommandHandler extends BaseCommandHandler {
  // =========================================================================
  // PHASE 4: Helper Functions
  // =========================================================================

  /**
   * Detects if a component is full-width (100% or similar)
   * Used to warn users about margin overflow on full-width containers
   */
  private isFullWidthContainer(component: Component): boolean {
    // Check size.width
    const sizeWidth = component.size?.width;
    if (sizeWidth) {
      const widthStr = String(sizeWidth);
      if (widthStr === '100%' || widthStr === '100vw' || widthStr.includes('100')) {
        return true;
      }
      // Check for large percentage values (90%+)
      const percentMatch = widthStr.match(/^(\d+)%$/);
      if (percentMatch && parseInt(percentMatch[1], 10) >= 90) {
        return true;
      }
    }

    // Check style.width
    const styleWidth = component.style?.width;
    if (styleWidth) {
      const widthStr = String(styleWidth);
      if (widthStr === '100%' || widthStr === '100vw') {
        return true;
      }
      const percentMatch = widthStr.match(/^(\d+)%$/);
      if (percentMatch && parseInt(percentMatch[1], 10) >= 90) {
        return true;
      }
    }

    // Check responsive constraints
    const constraints = component.responsivePosition?.constraints;
    const constraintWidth = constraints && typeof constraints === 'object'
      ? Reflect.get(constraints, 'width')
      : undefined;
    if (constraintWidth) {
      const widthStr = String(constraintWidth);
      if (widthStr === '100%' || widthStr === '100vw') {
        return true;
      }
    }

    // Default assumption: root flex containers are typically full-width
    // Check if parent is null/undefined (root container)
    if (!component.parentId) {
      return true;
    }

    return false;
  }

  /**
   * Generates a warning message for margin on full-width containers
   */
  private getFullWidthMarginWarning(marginValue: string): string {
    return `⚠️ Warning: Adding uniform margin (${marginValue}) to a full-width container will cause horizontal overflow.\n\n` +
      `Suggestions:\n` +
      `• Use "padding ${marginValue.replace('px', '')}" instead for internal spacing\n` +
      `• Use "margin top ${marginValue.replace('px', '')}" for vertical-only margin\n` +
      `• Use "margin horizontal auto" to center with auto margins\n` +
      `• Set a fixed width first, then apply margin`;
  }

  private getFlexProps(component: Component): FlexContainerProps {
    return this.typedProperties(component, isFlexContainerProps) ?? {};
  }

  private getFlexStyle(component: Component): FlexContainerStyleProps {
    return this.typedStyle(component, isFlexContainerStyleProps) ?? {};
  }

  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Full page/height patterns - IMPORTANT: Use 'size' not 'style' for dimensions
    // Components fill their wrapper, so size controls the wrapper dimensions
    this.commandPatterns.set(
      /^(?:make |set )?(?:the )?(?:height |it )?full[\s-]?page$/i,
      (_match, _component) => ({ size: { height: '100vh' }, properties: { minHeight: '100vh' } })
    );

    this.commandPatterns.set(
      /^(?:make |set )?(?:the )?height\s+full[\s-]?page$/i,
      (_match, _component) => ({ size: { height: '100vh' }, properties: { minHeight: '100vh' } })
    );

    this.commandPatterns.set(
      /^(?:make |set )?(?:it |the container )?100vh$/i,
      (_match, _component) => ({ size: { height: '100vh' }, properties: { minHeight: '100vh' } })
    );

    this.commandPatterns.set(
      /^(?:make |set )?full[\s-]?height$/i,
      (_match, _component) => ({ size: { height: '100%' }, properties: { minHeight: '100%' } })
    );

    this.commandPatterns.set(
      /^(?:make |set )?full[\s-]?screen$/i,
      (_match, _component) => ({ size: { height: '100vh', width: '100vw' } })
    );

    // Flexbox direction patterns
    this.commandPatterns.set(
      /^(?:set |make )?(?:flex[\s-]?)?direction\s+(?:to\s+)?(row|column|row-reverse|column-reverse)$/i,
      (match, _component) => ({ properties: { flexDirection: match[1].toLowerCase().replace(' ', '-') } })
    );

    this.commandPatterns.set(
      /^(?:make |set )?(?:it |layout )?horizontal$/i,
      (_match, _component) => ({ properties: { flexDirection: 'row' } })
    );

    this.commandPatterns.set(
      /^(?:make |set )?(?:it |layout )?vertical$/i,
      (_match, _component) => ({ properties: { flexDirection: 'column' } })
    );

    this.commandPatterns.set(
      /^stack\s+(?:items |children )?(?:horizontally|horizontal)$/i,
      (_match, _component) => ({ properties: { flexDirection: 'row' } })
    );

    this.commandPatterns.set(
      /^stack\s+(?:items |children )?(?:vertically|vertical)$/i,
      (_match, _component) => ({ properties: { flexDirection: 'column' } })
    );

    // Justify content patterns
    this.commandPatterns.set(
      /^(?:set |make )?justify[\s-]?content\s+(?:to\s+)?(flex-start|flex-end|center|space-between|space-around|space-evenly)$/i,
      (match, _component) => ({ properties: { justifyContent: match[1].toLowerCase().replace(' ', '-') } })
    );

    this.commandPatterns.set(
      /^(?:align |justify )?(?:items |children )?(?:to |at )?(?:the )?center$/i,
      (_match, _component) => ({ properties: { justifyContent: 'center', alignItems: 'center' } })
    );

    this.commandPatterns.set(
      /^center\s+(?:all |everything|items|children)$/i,
      (_match, _component) => ({ properties: { justifyContent: 'center', alignItems: 'center' } })
    );

    this.commandPatterns.set(
      /^space\s+(?:items |children )?evenly$/i,
      (_match, _component) => ({ properties: { justifyContent: 'space-evenly' } })
    );

    this.commandPatterns.set(
      /^space\s+between$/i,
      (_match, _component) => ({ properties: { justifyContent: 'space-between' } })
    );

    // Align items patterns
    this.commandPatterns.set(
      /^(?:set |make )?align[\s-]?items\s+(?:to\s+)?(flex-start|flex-end|center|stretch|baseline)$/i,
      (match, _component) => ({ properties: { alignItems: match[1].toLowerCase().replace(' ', '-') } })
    );

    this.commandPatterns.set(
      /^align\s+(?:to |at )?(?:the )?top$/i,
      (_match, _component) => ({ properties: { alignItems: 'flex-start' } })
    );

    this.commandPatterns.set(
      /^align\s+(?:to |at )?(?:the )?bottom$/i,
      (_match, _component) => ({ properties: { alignItems: 'flex-end' } })
    );

    this.commandPatterns.set(
      /^stretch\s+(?:items|children)$/i,
      (_match, _component) => ({ properties: { alignItems: 'stretch' } })
    );

    // Gap patterns
    // NOTE: Must set BOTH properties.gap AND style.gap for FlexContainer to apply
    this.commandPatterns.set(
      /^(?:set |add )?gap\s+(?:to\s+)?(\d+)(?:px)?$/i,
      (match, _component) => {
        const value = parseInt(match[1], 10);
        return { properties: { gap: value }, style: { gap: `${value}px` } };
      }
    );

    this.commandPatterns.set(
      /^(?:set |add )?spacing\s+(?:to\s+)?(\d+)(?:px)?$/i,
      (match, _component) => {
        const value = parseInt(match[1], 10);
        return { properties: { gap: value }, style: { gap: `${value}px` } };
      }
    );

    this.commandPatterns.set(
      /^no\s+gap$/i,
      (_match, _component) => ({ properties: { gap: 0 }, style: { gap: '0px' } })
    );

    this.commandPatterns.set(
      /^remove\s+(?:gap|spacing)$/i,
      (_match, _component) => ({ properties: { gap: 0 }, style: { gap: '0px' } })
    );

    // Flex wrap patterns
    this.commandPatterns.set(
      /^(?:enable |allow )?wrap$/i,
      (_match, _component) => ({ properties: { flexWrap: 'wrap' } })
    );

    this.commandPatterns.set(
      /^(?:disable |no )?wrap$/i,
      (_match, _component) => ({ properties: { flexWrap: 'nowrap' } })
    );

    this.commandPatterns.set(
      /^wrap\s+items$/i,
      (_match, _component) => ({ properties: { flexWrap: 'wrap' } })
    );

    // Padding patterns for containers
    // NOTE: Must set BOTH properties.padding AND style.padding for FlexContainer to apply
    // NOTE: Must clear individual padding properties to avoid CSS shorthand conflicts
    this.commandPatterns.set(
      /^(?:add |set )?(?:inner )?padding\s+(?:to\s+)?(\d+)(?:px)?$/i,
      (match) => {
        const value = parseInt(match[1], 10);
        return {
          properties: { padding: value },
          style: {
            padding: `${value}px`,
            // Clear individual properties to avoid shorthand/longhand conflicts
            paddingTop: undefined,
            paddingRight: undefined,
            paddingBottom: undefined,
            paddingLeft: undefined
          }
        };
      }
    );

    this.commandPatterns.set(
      /^(?:add |set )?container\s+padding\s+(?:to\s+)?(\d+)(?:px)?$/i,
      (match) => {
        const value = parseInt(match[1], 10);
        return {
          properties: { padding: value },
          style: {
            padding: `${value}px`,
            // Clear individual properties to avoid shorthand/longhand conflicts
            paddingTop: undefined,
            paddingRight: undefined,
            paddingBottom: undefined,
            paddingLeft: undefined
          }
        };
      }
    );

    // =========================================================================
    // PHASE 1: Background & Color Enhancements
    // =========================================================================

    // 1.1.1 Named color backgrounds (14 CSS named colors)
    // Pattern: "bg red", "background blue", "make background green"
    // NOTE: Must clear style.background to override any existing gradients
    this.commandPatterns.set(
      /^(?:set |make )?(?:the )?(?:background|bg)(?:\s+(?:color\s+)?(?:to\s+)?)?\s+(red|blue|green|yellow|orange|purple|pink|black|white|gray|grey|brown|cyan|magenta)$/i,
      (match) => {
        const color = match[1].toLowerCase();
        return {
          properties: { backgroundColor: color },
          style: { background: 'none', backgroundColor: color }  // Clear gradient + sync style
        };
      }
    );

    // 1.1.2 Hex color backgrounds (#RGB, #RRGGBB, #RRGGBBAA)
    // Pattern: "bg #ff5500", "background #333", "bg #ff550080"
    this.commandPatterns.set(
      /^(?:set |make )?(?:the )?(?:background|bg)(?:\s+(?:color\s+)?(?:to\s+)?)?\s*(#[0-9A-Fa-f]{3,8})$/i,
      (match) => {
        const color = match[1];
        return {
          properties: { backgroundColor: color },
          style: { background: 'none', backgroundColor: color }  // Clear gradient + sync style
        };
      }
    );

    // 1.1.3 RGB/RGBA color backgrounds
    // Pattern: "bg rgb(255, 100, 50)", "background rgba(0, 0, 0, 0.5)"
    this.commandPatterns.set(
      /^(?:set |make )?(?:the )?(?:background|bg)(?:\s+(?:color\s+)?(?:to\s+)?)?\s*(rgba?\([^)]+\))$/i,
      (match) => {
        const color = match[1];
        return {
          properties: { backgroundColor: color },
          style: { background: 'none', backgroundColor: color }  // Clear gradient + sync style
        };
      }
    );

    // 1.1.4 Generic background with parseColorValue (catch-all for complex colors)
    // Pattern: "set background color to X", "change bg to X"
    // NOTE: Excludes "darker" and "lighter" which are handled by Phase 6 relative value patterns
    this.commandPatterns.set(
      /^(?:set |change |make )?(?:the )?(?:background|bg)(?:\s+color)?\s+(?:to\s+)?(.+)$/i,
      (match) => {
        const colorInput = match[1].trim().toLowerCase();

        // Skip "darker" and "lighter" - these are handled by Phase 6 relative value patterns
        if (colorInput === 'darker' || colorInput === 'lighter') {
          return null;
        }

        // First check brand colors
        const brandColor = BRAND_COLORS[colorInput];
        if (brandColor) {
          return {
            properties: { backgroundColor: brandColor },
            style: { background: 'none', backgroundColor: brandColor }  // Clear gradient + sync style
          };
        }
        // Then try parseColorValue
        const parsedColor = this.parseColorValue(match[1]);
        if (parsedColor) {
          return {
            properties: { backgroundColor: parsedColor },
            style: {
              background: 'none',
              backgroundColor: parsedColor,
              ...(parsedColor === 'transparent' ? { opacity: 1 } : {})
            }
          };
        }
        return null;
      }
    );

    // 1.1.5 Direct color word as background (container context)
    // Pattern: "make it red", "make the container blue"
    this.commandPatterns.set(
      /^(?:make (?:it |the container )?)?(red|blue|green|yellow|orange|purple|pink|black|white|gray|grey|brown|cyan|magenta)$/i,
      (match) => {
        const color = match[1].toLowerCase();
        return {
          properties: { backgroundColor: color },
          style: { background: 'none', backgroundColor: color }  // Clear gradient + sync style
        };
      }
    );

    // Legacy transparent/white/gray shortcuts (keep for backwards compatibility)
    this.commandPatterns.set(
      /^(?:set |make )?(?:background |bg )?transparent$/i,
      () => {
        return {
          properties: { backgroundColor: 'transparent' },
          style: { background: 'none', backgroundColor: 'transparent' }  // Clear gradient + sync style
        };
      }
    );

    // 1.2.1 TerraMatrix Brand Color backgrounds
    // Pattern: "bg forest green", "background sage", "bg primary"
    this.commandPatterns.set(
      /^(?:set |make )?(?:the )?(?:background|bg)\s+(forest\s*green|sage\s*green|seafoam(?:\s*green)?|blue\s*grotto|desert\s*sun|chartreuse|charcoal|champagne|mid\s*gr[ae]y|light\s*gr[ae]y|forest|sage|grotto|desert|primary|secondary|info|warning|highlight|cream)$/i,
      (match) => {
        const colorKey = match[1].toLowerCase().replace(/\s+/g, ' ').trim();
        const brandColor = BRAND_COLORS[colorKey];
        if (brandColor) {
          return {
            properties: { backgroundColor: brandColor },
            style: { background: 'none', backgroundColor: brandColor }  // Clear gradient + sync style
          };
        }
        return null;
      }
    );

    // 1.3.1 Linear gradient backgrounds
    // Pattern: "gradient from blue to green", "gradient from forest green to cool gray"
    // Uses (\w+(?:\s+(?!left|right|top|bottom)\w+)?) to support multi-word color names
    // The negative lookahead (?!left|right|top|bottom) prevents matching direction words as color names
    this.commandPatterns.set(
      /^(?:add |set )?(?:linear\s+)?gradient\s+(?:from\s+)?(\w+(?:\s+(?!left\b|right\b|top\b|bottom\b)\w+)?)\s+to\s+(\w+(?:\s+(?!left\b|right\b|top\b|bottom\b)\w+)?)(?:\s+(left|right|top|bottom|top left|top right|bottom left|bottom right))?$/i,
      (match) => {
        // Check brand colors FIRST, then parseColorValue, then use raw value as fallback
        // This ensures multi-word brand colors like "forest green" take precedence over generic "green"
        const color1 = BRAND_COLORS[match[1].toLowerCase()] || this.parseColorValue(match[1]) || match[1];
        const color2 = BRAND_COLORS[match[2].toLowerCase()] || this.parseColorValue(match[2]) || match[2];
        const direction = match[3] ? `to ${match[3]}` : 'to right';
        const gradient = `linear-gradient(${direction}, ${color1}, ${color2})`;
        return { style: { background: gradient } };
      }
    );

    // 1.3.2 Linear gradient with direction first
    // Pattern: "horizontal gradient red orange", "vertical gradient forest green sage"
    // Uses (\w+(?:\s+\w+)?) to support multi-word color names
    this.commandPatterns.set(
      /^(?:add |set )?(horizontal|vertical)\s+gradient\s+(\w+(?:\s+\w+)?)\s+(?:to\s+)?(\w+(?:\s+\w+)?)$/i,
      (match) => {
        const direction = match[1].toLowerCase() === 'horizontal' ? 'to right' : 'to bottom';
        // Check brand colors FIRST, then parseColorValue, then use raw value as fallback
        const color1 = BRAND_COLORS[match[2].toLowerCase()] || this.parseColorValue(match[2]) || match[2];
        const color2 = BRAND_COLORS[match[3].toLowerCase()] || this.parseColorValue(match[3]) || match[3];
        const gradient = `linear-gradient(${direction}, ${color1}, ${color2})`;
        return { style: { background: gradient } };
      }
    );

    // 1.3.3 Radial gradient backgrounds
    // Pattern: "radial gradient red to yellow", "radial gradient from forest green to cool gray"
    // Uses (\w+(?:\s+\w+)?) to support multi-word color names
    this.commandPatterns.set(
      /^(?:add |set )?radial\s+gradient\s+(?:from\s+)?(\w+(?:\s+\w+)?)\s+to\s+(\w+(?:\s+\w+)?)$/i,
      (match) => {
        // Check brand colors FIRST, then parseColorValue, then use raw value as fallback
        const color1 = BRAND_COLORS[match[1].toLowerCase()] || this.parseColorValue(match[1]) || match[1];
        const color2 = BRAND_COLORS[match[2].toLowerCase()] || this.parseColorValue(match[2]) || match[2];
        const gradient = `radial-gradient(${color1}, ${color2})`;
        return { style: { background: gradient } };
      }
    );

    // 1.4.1 Background opacity percentage
    // Pattern: "bg opacity 50%", "background 80% opacity"
    this.commandPatterns.set(
      /^(?:set )?(?:background|bg)\s+opacity\s+(\d+)%?$/i,
      (match, component) => {
        const opacity = parseInt(match[1], 10) / 100;

        // If there's a gradient, apply CSS opacity to preserve the gradient
        if (this.hasGradient(component)) {
          return { style: { opacity: opacity } };
        }

        // No gradient - apply semi-transparent background color
        const currentBg = this.getCurrentBackgroundColor(component);
        const rgbaColor = this.colorToRgba(currentBg, opacity);
        return {
          properties: { backgroundColor: rgbaColor },
          style: { background: 'none', backgroundColor: rgbaColor }  // Clear gradient + sync style
        };
      }
    );

    // 1.4.2 Semi-transparent shortcut
    // Pattern: "semi-transparent", "semi transparent background"
    this.commandPatterns.set(
      /^(?:make |set )?(?:it )?semi[\s-]?transparent(?:\s+(?:background|bg))?$/i,
      (_match, component) => {
        // If there's a gradient, apply CSS opacity to preserve the gradient
        if (this.hasGradient(component)) {
          return { style: { opacity: 0.5 } };
        }

        const semiColor = 'rgba(255, 255, 255, 0.5)';
        return {
          properties: { backgroundColor: semiColor },
          style: { background: 'none', backgroundColor: semiColor }  // Clear gradient + sync style
        };
      }
    );

    // 1.4.2b Percentage transparency shortcut
    // Pattern: "50% transparent", "50% opacity", "make it 30% transparent", "50% transparency"
    this.commandPatterns.set(
      /^(?:make |set )?(?:it |the )?(\d+)%?\s+(?:transparent|transparency|opacity)(?:\s+(?:background|bg))?$/i,
      (match, component) => {
        const percentage = parseInt(match[1], 10);
        const opacity = percentage / 100;

        // If there's a gradient, apply CSS opacity to preserve the gradient
        if (this.hasGradient(component)) {
          return { style: { opacity: opacity } };
        }

        // No gradient - apply semi-transparent background color
        const currentBg = this.getCurrentBackgroundColor(component);
        const rgbaColor = this.colorToRgba(currentBg, opacity);
        return {
          properties: { backgroundColor: rgbaColor },
          style: { background: 'none', backgroundColor: rgbaColor }  // Clear gradient + sync style
        };
      }
    );

    // 1.4.2c Transparency/opacity to percentage
    // Pattern: "transparency to 50%", "opacity to 80", "set transparency to 30%"
    this.commandPatterns.set(
      /^(?:set |change )?(?:the )?(?:opacity|transparency)\s+(?:to\s+)?(\d+)%?$/i,
      (match, component) => {
        const percentage = parseInt(match[1], 10);
        const opacity = percentage / 100;

        // If there's a gradient, apply CSS opacity to preserve the gradient
        if (this.hasGradient(component)) {
          return { style: { opacity: opacity } };
        }

        // No gradient - apply semi-transparent background color
        const currentBg = this.getCurrentBackgroundColor(component);
        const rgbaColor = this.colorToRgba(currentBg, opacity);
        return {
          properties: { backgroundColor: rgbaColor },
          style: { background: 'none', backgroundColor: rgbaColor }  // Clear gradient + sync style
        };
      }
    );

    // 1.4.3 Faint/subtle background with optional color
    // Pattern: "faint background", "subtle bg green"
    this.commandPatterns.set(
      /^(?:set |make )?(?:faint|subtle)\s+(?:background|bg)(?:\s+(green|blue|orange))?$/i,
      (match) => {
        const colorKey = match[1]?.toLowerCase() || 'default';
        const faintColor = FAINT_COLORS[colorKey] || FAINT_COLORS['default'];
        return {
          properties: { backgroundColor: faintColor },
          style: { background: 'none', backgroundColor: faintColor }  // Clear gradient + sync style
        };
      }
    );

    // 1.5 Remove/Reset Patterns
    // =========================================================================

    // 1.5.1 Remove transparency/opacity - reset to fully opaque
    // Pattern: "remove transparency", "remove opacity", "clear transparency", "no transparency", "make opaque", "fully opaque"
    // Handles both CSS opacity (for gradients) and rgba backgroundColor (for solid colors)
    this.commandPatterns.set(
      /^(?:remove|clear|reset|no)\s+(?:the\s+)?(?:transparency|opacity)$/i,
      (_match, component) => {
        return this.makeOpaque(component);
      }
    );

    this.commandPatterns.set(
      /^(?:make\s+)?(?:it\s+)?(?:fully\s+)?opaque$/i,
      (_match, component) => {
        return this.makeOpaque(component);
      }
    );

    this.commandPatterns.set(
      /^(?:set\s+)?(?:opacity|transparency)\s+(?:to\s+)?100%?$/i,
      (_match, component) => {
        return this.makeOpaque(component);
      }
    );

    // 1.5.2 Remove background color - make transparent
    // Pattern: "remove background", "remove bg", "clear background", "no background", "transparent background"
    this.commandPatterns.set(
      /^(?:remove|clear|reset)\s+(?:the\s+)?(?:background|bg)(?:\s+color)?$/i,
      (_match, _component) => {
        return {
          properties: { backgroundColor: 'transparent' },
          style: { background: 'none', backgroundColor: 'transparent', opacity: 1 }
        };
      }
    );

    this.commandPatterns.set(
      /^(?:no|remove)\s+(?:background|bg)\s*(?:color)?$/i,
      (_match, _component) => {
        return {
          properties: { backgroundColor: 'transparent' },
          style: { background: 'none', backgroundColor: 'transparent', opacity: 1 }
        };
      }
    );

    this.commandPatterns.set(
      /^(?:make\s+)?(?:the\s+)?(?:background|bg)\s+transparent$/i,
      (_match, _component) => {
        return {
          properties: { backgroundColor: 'transparent' },
          style: { background: 'none', backgroundColor: 'transparent', opacity: 1 }
        };
      }
    );

    // 1.5.3 Remove gradient specifically
    // Pattern: "remove gradient", "clear gradient", "no gradient"
    this.commandPatterns.set(
      /^(?:remove|clear|reset|no)\s+(?:the\s+)?gradient$/i,
      (_match, component) => {
        // Keep the backgroundColor if it exists, just remove the gradient
        const currentBg = this.getCurrentBackgroundColor(component);
        const bgColor = currentBg !== '#ffffff' ? currentBg : 'transparent';
        return {
          properties: { backgroundColor: bgColor },
          style: { background: 'none', backgroundColor: bgColor }
        };
      }
    );

    // =========================================================================
    // END PHASE 1: Background & Color Enhancements
    // =========================================================================

    // =========================================================================
    // PHASE 2: Border Control System
    // =========================================================================

    // -------------------------------------------------------------------------
    // 2.1 Border Width Patterns
    // -------------------------------------------------------------------------

    // 2.1.1 Numeric border width
    // Pattern: "border 2px", "border width 3", "set border 4px"
    this.commandPatterns.set(
      /^(?:add |set )?border(?:\s+width)?\s+(\d+)(?:px)?$/i,
      (match, component) => {
        const width = `${match[1]}px`;
        // Preserve existing border color/style if set, otherwise use defaults
        const existingColor = this.getFlexStyle(component)?.borderColor;
        const existingStyle = this.getFlexStyle(component)?.borderStyle;
        return {
          style: {
            borderWidth: width,
            borderStyle: existingStyle || 'solid',
            borderColor: existingColor || DEFAULT_BORDER_COLOR
          }
        };
      }
    );

    // 2.1.2 Named border widths
    // Pattern: "thin border", "thick border", "add medium border"
    this.commandPatterns.set(
      /^(?:add |set )?(thin|medium|thick|heavy)\s+border$/i,
      (match, component) => {
        const widthName = match[1].toLowerCase();
        const width = BORDER_WIDTHS[widthName];
        // Preserve existing border color/style if set, otherwise use defaults
        const existingColor = this.getFlexStyle(component)?.borderColor;
        const existingStyle = this.getFlexStyle(component)?.borderStyle;
        return {
          style: {
            borderWidth: width,
            borderStyle: existingStyle || 'solid',
            borderColor: existingColor || DEFAULT_BORDER_COLOR
          }
        };
      }
    );

    // 2.1.3 Simple "add border" (default 1px solid)
    // Pattern: "add border"
    this.commandPatterns.set(
      /^add\s+border$/i,
      () => {
        // Use individual properties so subsequent commands can preserve them
        // Also clear the shorthand 'border' property to avoid conflicts
        return {
          style: {
            border: undefined, // Clear shorthand to avoid "border: none" taking precedence
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: DEFAULT_BORDER_COLOR
          }
        };
      }
    );

    // 2.1.4 Remove border patterns
    // Pattern: "remove border", "hide border", "no border"
    this.commandPatterns.set(
      /^(?:(?:hide|remove|delete)\s+(?:the\s+)?|no\s+)border$/i,
      () => {
        // Clear all border properties to ensure border is fully removed
        return {
          style: {
            border: 'none',
            borderWidth: undefined,
            borderStyle: undefined,
            borderColor: undefined
          }
        };
      }
    );

    // -------------------------------------------------------------------------
    // 2.2 Border Style Patterns
    // -------------------------------------------------------------------------

    // 2.2.1 Explicit border style
    // Pattern: "border style solid", "set border-style dashed"
    this.commandPatterns.set(
      /^(?:set )?border[\s-]?style\s+(?:to\s+)?(solid|dashed|dotted|double|none|hidden)$/i,
      (match) => {
        const style = match[1].toLowerCase();
        return { style: { borderStyle: style } };
      }
    );

    // 2.2.2 Adjective-style patterns
    // Pattern: "dashed border", "dotted border", "solid border"
    this.commandPatterns.set(
      /^(?:make |add )?(?:a )?(solid|dashed|dotted|double)\s+border$/i,
      (match, component) => {
        const style = match[1].toLowerCase();
        // Preserve existing border width/color if set, otherwise use defaults
        const existingWidth = this.getFlexStyle(component)?.borderWidth;
        const existingColor = this.getFlexStyle(component)?.borderColor;
        return {
          style: {
            borderStyle: style,
            borderWidth: existingWidth || '1px',
            borderColor: existingColor || DEFAULT_BORDER_COLOR
          }
        };
      }
    );

    // 2.2.3 "Make it X" patterns for border style
    // Pattern: "make it dashed", "make the border dotted"
    this.commandPatterns.set(
      /^make\s+(?:it |the\s+border\s+)?(dashed|dotted|solid|double)$/i,
      (match) => {
        const style = match[1].toLowerCase();
        return { style: { borderStyle: style } };
      }
    );

    // 2.2.4 Border-first style pattern (simple "border dashed" command)
    // Pattern: "border dashed", "border dotted", "border solid"
    this.commandPatterns.set(
      /^border\s+(dashed|dotted|solid|double)$/i,
      (match, component) => {
        const style = match[1].toLowerCase();
        // Preserve existing border width/color if set, otherwise use defaults
        const existingWidth = this.getFlexStyle(component)?.borderWidth;
        const existingColor = this.getFlexStyle(component)?.borderColor;
        return {
          style: {
            borderStyle: style,
            borderWidth: existingWidth || '1px',
            borderColor: existingColor || DEFAULT_BORDER_COLOR
          }
        };
      }
    );

    // -------------------------------------------------------------------------
    // 2.3 Border Color Patterns
    // -------------------------------------------------------------------------

    // 2.3.1 Explicit border color
    // Pattern: "border color red", "set border color to blue", "border color #ff5500"
    this.commandPatterns.set(
      /^(?:set )?border\s+color\s+(?:to\s+)?(.+)$/i,
      (match) => {
        const colorInput = match[1].trim().toLowerCase();
        // Check brand colors first
        const brandColor = BRAND_COLORS[colorInput];
        if (brandColor) {
          return { style: { borderColor: brandColor } };
        }
        // Try parseColorValue
        const parsedColor = this.parseColorValue(match[1]);
        if (parsedColor) {
          return { style: { borderColor: parsedColor } };
        }
        // Use raw value as fallback
        return { style: { borderColor: colorInput } };
      }
    );

    // 2.3.2 Color-first border pattern
    // Pattern: "red border", "blue border", "forest green border"
    this.commandPatterns.set(
      /^(red|blue|green|yellow|orange|purple|pink|black|white|gray|grey|forest\s*green|sage\s*green|seafoam|blue\s*grotto|desert\s*sun|chartreuse|charcoal|champagne|cream|primary|secondary|info|warning)\s+border$/i,
      (match, component) => {
        const colorKey = match[1].toLowerCase().replace(/\s+/g, ' ').trim();
        const brandColor = BRAND_COLORS[colorKey];
        const color = brandColor || this.parseColorValue(match[1]) || match[1];
        // Preserve existing border width/style if set, otherwise use defaults
        const existingWidth = this.getFlexStyle(component)?.borderWidth;
        const existingStyle = this.getFlexStyle(component)?.borderStyle;
        return {
          style: {
            borderColor: color,
            borderWidth: existingWidth || '1px',
            borderStyle: existingStyle || 'solid'
          }
        };
      }
    );

    // 2.3.3 Border-first color pattern (simple "border red" command)
    // Pattern: "border red", "border blue", "border forest green"
    this.commandPatterns.set(
      /^border\s+(red|blue|green|yellow|orange|purple|pink|black|white|gray|grey|forest\s*green|sage\s*green|seafoam|blue\s*grotto|desert\s*sun|chartreuse|charcoal|champagne|cream|primary|secondary|info|warning)$/i,
      (match, component) => {
        const colorKey = match[1].toLowerCase().replace(/\s+/g, ' ').trim();
        const brandColor = BRAND_COLORS[colorKey];
        const color = brandColor || this.parseColorValue(match[1]) || match[1];
        // Preserve existing border width/style if set, otherwise use defaults
        const existingWidth = this.getFlexStyle(component)?.borderWidth;
        const existingStyle = this.getFlexStyle(component)?.borderStyle;
        return {
          style: {
            borderColor: color,
            borderWidth: existingWidth || '1px',
            borderStyle: existingStyle || 'solid'
          }
        };
      }
    );

    // -------------------------------------------------------------------------
    // 2.4 Border Shorthand Patterns
    // -------------------------------------------------------------------------

    // 2.4.1 Full border shorthand (width style color)
    // Pattern: "border 2px solid red", "border 3 dashed blue"
    this.commandPatterns.set(
      /^(?:set )?border\s+(\d+)(?:px)?\s+(solid|dashed|dotted|double)\s+(.+)$/i,
      (match) => {
        const width = `${match[1]}px`;
        const style = match[2].toLowerCase();
        const colorInput = match[3].trim().toLowerCase();
        const brandColor = BRAND_COLORS[colorInput];
        const color = brandColor || this.parseColorValue(match[3]) || match[3];
        return { style: { border: `${width} ${style} ${color}` } };
      }
    );

    // 2.4.2 Border width + color (assumes solid)
    // Pattern: "border 2px red", "border 1 blue"
    this.commandPatterns.set(
      /^border\s+(\d+)(?:px)?\s+(red|blue|green|yellow|orange|purple|pink|black|white|gray|grey|#[0-9A-Fa-f]{3,8}|forest\s*green|sage|seafoam|primary|secondary|info|warning)$/i,
      (match) => {
        const width = `${match[1]}px`;
        const colorInput = match[2].trim().toLowerCase();
        const brandColor = BRAND_COLORS[colorInput.replace(/\s+/g, ' ')];
        const color = brandColor || this.parseColorValue(match[2]) || match[2];
        return { style: { border: `${width} solid ${color}` } };
      }
    );

    // 2.4.3 "bd" shortcut pattern for power users
    // Pattern: "bd 2", "bd 2 solid", "bd 2 dashed red"
    this.commandPatterns.set(
      /^bd\s+(\d+)\s*(solid|dashed|dotted|double)?\s*(.*)$/i,
      (match) => {
        const width = `${match[1]}px`;
        const style = match[2]?.toLowerCase() || 'solid';
        let color = DEFAULT_BORDER_COLOR;
        if (match[3]) {
          const colorInput = match[3].trim().toLowerCase();
          const brandColor = BRAND_COLORS[colorInput.replace(/\s+/g, ' ')];
          color = brandColor || this.parseColorValue(match[3]) || colorInput || DEFAULT_BORDER_COLOR;
        }
        return { style: { border: `${width} ${style} ${color}` } };
      }
    );

    // -------------------------------------------------------------------------
    // 2.5 Individual Border Side Patterns
    // -------------------------------------------------------------------------

    // 2.5.1 Side-specific border with value
    // Pattern: "border top 2px", "border-left 3 solid red"
    this.commandPatterns.set(
      /^(?:set )?border[\s-]?(top|right|bottom|left)\s+(\d+)(?:px)?(?:\s+(solid|dashed|dotted|double))?(?:\s+(.+))?$/i,
      (match) => {
        const side = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
        const width = `${match[2]}px`;
        const style = match[3]?.toLowerCase() || 'solid';
        const colorInput = match[4]?.trim().toLowerCase();
        let color = DEFAULT_BORDER_COLOR;
        if (colorInput) {
          const brandColor = BRAND_COLORS[colorInput.replace(/\s+/g, ' ')];
          color = brandColor || this.parseColorValue(match[4]) || colorInput;
        }
        return { style: { [`border${side}`]: `${width} ${style} ${color}` } };
      }
    );

    // 2.5.2 Side-first syntax
    // Pattern: "top border", "left border 2px", "right border red"
    this.commandPatterns.set(
      /^(top|right|bottom|left)\s+border(?:\s+(\d+)(?:px)?)?(?:\s+(red|blue|green|yellow|orange|purple|pink|black|white|gray|grey|#[0-9A-Fa-f]{3,8}))?$/i,
      (match) => {
        const side = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
        const width = match[2] ? `${match[2]}px` : '1px';
        let color = DEFAULT_BORDER_COLOR;
        if (match[3]) {
          color = this.parseColorValue(match[3]) || match[3];
        }
        return { style: { [`border${side}`]: `${width} solid ${color}` } };
      }
    );

    // 2.5.3 Horizontal/Vertical borders
    // Pattern: "horizontal borders", "vertical borders 2px"
    this.commandPatterns.set(
      /^(horizontal|vertical)\s+borders?(?:\s+(\d+)(?:px)?)?$/i,
      (match) => {
        const width = match[2] ? `${match[2]}px` : '1px';
        const border = `${width} solid ${DEFAULT_BORDER_COLOR}`;
        if (match[1].toLowerCase() === 'horizontal') {
          return {
            style: {
              borderTop: border,
              borderBottom: border,
              borderLeft: 'none',
              borderRight: 'none'
            }
          };
        }
        return {
          style: {
            borderLeft: border,
            borderRight: border,
            borderTop: 'none',
            borderBottom: 'none'
          }
        };
      }
    );

    // 2.5.4 Remove specific border side
    // Pattern: "remove top border", "no left border"
    this.commandPatterns.set(
      /^(?:remove|no)\s+(top|right|bottom|left)\s+border$/i,
      (match) => {
        const side = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
        return { style: { [`border${side}`]: 'none' } };
      }
    );

    // -------------------------------------------------------------------------
    // 2.6 Enhanced Border Radius Patterns
    // -------------------------------------------------------------------------

    // 2.6.1 Radius presets
    // Pattern: "radius sm", "border radius lg", "set radius xl"
    // Clear individual corners to avoid conflicts with existing values
    this.commandPatterns.set(
      /^(?:set |make )?(?:border[\s-]?)?radius\s+(none|xs|sm|md|lg|xl|2xl|full)$/i,
      (match) => {
        const preset = match[1].toLowerCase();
        const radius = RADIUS_PRESETS[preset];
        return {
          style: {
            borderRadius: radius,
            borderTopLeftRadius: undefined,
            borderTopRightRadius: undefined,
            borderBottomLeftRadius: undefined,
            borderBottomRightRadius: undefined
          }
        };
      }
    );

    // 2.6.2 "rounded" with preset
    // Pattern: "rounded lg", "make rounded sm"
    // Clear individual corners to avoid conflicts with existing values
    this.commandPatterns.set(
      /^(?:make )?rounded\s+(none|xs|sm|md|lg|xl|2xl|full)$/i,
      (match) => {
        const preset = match[1].toLowerCase();
        const radius = RADIUS_PRESETS[preset];
        return {
          style: {
            borderRadius: radius,
            borderTopLeftRadius: undefined,
            borderTopRightRadius: undefined,
            borderBottomLeftRadius: undefined,
            borderBottomRightRadius: undefined
          }
        };
      }
    );

    // 2.6.3 Pill/circular shapes
    // Pattern: "pill", "pill shape", "circular", "fully rounded", "make it round"
    // Clear individual corners to avoid conflicts with existing values
    this.commandPatterns.set(
      /^(?:make\s+)?(?:it\s+)?(pill|circular|fully\s+rounded)(?:\s+shape)?$/i,
      () => {
        return {
          style: {
            borderRadius: '9999px',
            borderTopLeftRadius: undefined,
            borderTopRightRadius: undefined,
            borderBottomLeftRadius: undefined,
            borderBottomRightRadius: undefined
          }
        };
      }
    );

    // 2.6.4 Percentage radius
    // Pattern: "radius 50%", "border radius 25%"
    // Clear individual corners to avoid conflicts with existing values
    this.commandPatterns.set(
      /^(?:set )?(?:border[\s-]?)?radius\s+(\d+)%$/i,
      (match) => {
        const radius = `${match[1]}%`;
        return {
          style: {
            borderRadius: radius,
            borderTopLeftRadius: undefined,
            borderTopRightRadius: undefined,
            borderBottomLeftRadius: undefined,
            borderBottomRightRadius: undefined
          }
        };
      }
    );

    // 2.6.5 Individual corner radius
    // Pattern: "round top left 8px", "radius top-right 12", "border radius bottom left 16"
    // Clear shorthand and preserve other corners to avoid React warnings
    this.commandPatterns.set(
      /^(?:set |round )?(?:border[\s-]?)?radius\s+(top|bottom)[\s-]?(left|right)\s+(\d+)(?:px)?$/i,
      (match, component) => {
        const vertical = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
        const horizontal = match[2].charAt(0).toUpperCase() + match[2].slice(1).toLowerCase();
        const radius = `${match[3]}px`;
        const cornerKey = `border${vertical}${horizontal}Radius`;
        // Get existing radius to preserve other corners
        const existingRadius = this.getFlexStyle(component)?.borderRadius ||
          `${this.getFlexProps(component)?.borderRadius || 8}px`;
        // Get existing individual corner values or use the shorthand value
        const styleObj = this.getFlexStyle(component) ?? {};
        return {
          style: {
            borderRadius: undefined, // Clear shorthand to avoid conflicts
            borderTopLeftRadius: cornerKey === 'borderTopLeftRadius' ? radius : (styleObj.borderTopLeftRadius || existingRadius),
            borderTopRightRadius: cornerKey === 'borderTopRightRadius' ? radius : (styleObj.borderTopRightRadius || existingRadius),
            borderBottomLeftRadius: cornerKey === 'borderBottomLeftRadius' ? radius : (styleObj.borderBottomLeftRadius || existingRadius),
            borderBottomRightRadius: cornerKey === 'borderBottomRightRadius' ? radius : (styleObj.borderBottomRightRadius || existingRadius)
          }
        };
      }
    );

    // 2.6.6 Top/bottom rounded
    // Pattern: "rounded top", "rounded bottom 16px"
    // Clear shorthand borderRadius to avoid React warnings about mixing shorthand and individual properties
    this.commandPatterns.set(
      /^(?:round |rounded\s+)(top|bottom)(?:\s+(\d+)(?:px)?)?$/i,
      (match) => {
        const value = match[2] ? `${match[2]}px` : '8px';
        if (match[1].toLowerCase() === 'top') {
          return {
            style: {
              borderRadius: undefined, // Clear shorthand to avoid conflicts
              borderTopLeftRadius: value,
              borderTopRightRadius: value,
              borderBottomLeftRadius: '0',
              borderBottomRightRadius: '0'
            }
          };
        }
        return {
          style: {
            borderRadius: undefined,
            borderBottomLeftRadius: value,
            borderBottomRightRadius: value,
            borderTopLeftRadius: '0',
            borderTopRightRadius: '0'
          }
        };
      }
    );

    // 2.6.7 Left/right rounded
    // Pattern: "rounded left", "rounded right 16px"
    // Clear shorthand borderRadius to avoid React warnings about mixing shorthand and individual properties
    this.commandPatterns.set(
      /^(?:round |rounded\s+)(left|right)(?:\s+(\d+)(?:px)?)?$/i,
      (match) => {
        const value = match[2] ? `${match[2]}px` : '8px';
        if (match[1].toLowerCase() === 'left') {
          return {
            style: {
              borderRadius: undefined,
              borderTopLeftRadius: value,
              borderBottomLeftRadius: value,
              borderTopRightRadius: '0',
              borderBottomRightRadius: '0'
            }
          };
        }
        return {
          style: {
            borderRadius: undefined,
            borderTopRightRadius: value,
            borderBottomRightRadius: value,
            borderTopLeftRadius: '0',
            borderBottomLeftRadius: '0'
          }
        };
      }
    );

    // 2.6.8 Square corners / no radius
    // Pattern: "square corners", "square all corners", "make square", "no radius"
    this.commandPatterns.set(
      /^(?:make\s+)?(?:it\s+)?square(?:\s+(?:all\s+)?corners)?$/i,
      () => {
        // Use style with '0' string for consistency, clear individual corners
        return {
          style: {
            borderRadius: '0',
            borderTopLeftRadius: undefined,
            borderTopRightRadius: undefined,
            borderBottomLeftRadius: undefined,
            borderBottomRightRadius: undefined
          }
        };
      }
    );

    this.commandPatterns.set(
      /^(?:no|remove|reset)\s+(?:all\s+)?(?:border[\s-]?)?radius$/i,
      () => {
        // Use style with '0' string for consistency, clear individual corners
        return {
          style: {
            borderRadius: '0',
            borderTopLeftRadius: undefined,
            borderTopRightRadius: undefined,
            borderBottomLeftRadius: undefined,
            borderBottomRightRadius: undefined
          }
        };
      }
    );

    // 2.6.8b Numeric radius without "border" prefix
    // Pattern: "radius 6", "radius 20", "radius 8px"
    // Clear individual corners to avoid conflicts with existing values
    this.commandPatterns.set(
      /^radius\s+(\d+)(?:px)?$/i,
      (match) => {
        const radius = `${match[1]}px`;
        return {
          style: {
            borderRadius: radius,
            // Clear individual corners to avoid conflicts
            borderTopLeftRadius: undefined,
            borderTopRightRadius: undefined,
            borderBottomLeftRadius: undefined,
            borderBottomRightRadius: undefined
          }
        };
      }
    );

    // 2.6.8c Round all corners (reset to uniform radius)
    // Pattern: "round all corners", "round corners", "rounded corners"
    // This clears any individual corner values and sets a uniform radius
    this.commandPatterns.set(
      /^(?:round|rounded)\s+(?:all\s+)?corners$/i,
      () => {
        return {
          style: {
            borderRadius: '8px',
            // Clear individual corners to avoid conflicts
            borderTopLeftRadius: undefined,
            borderTopRightRadius: undefined,
            borderBottomLeftRadius: undefined,
            borderBottomRightRadius: undefined
          }
        };
      }
    );

    // 2.6.8d Remove square corners (make corners rounded)
    // Pattern: "remove square corners", "unsquare corners", "undo square corners"
    this.commandPatterns.set(
      /^(?:(?:remove|undo)\s+square|unsquare)\s*(?:corners)?$/i,
      () => {
        return {
          style: {
            borderRadius: '8px',
            // Clear individual corners to avoid conflicts
            borderTopLeftRadius: undefined,
            borderTopRightRadius: undefined,
            borderBottomLeftRadius: undefined,
            borderBottomRightRadius: undefined
          }
        };
      }
    );

    // 2.6.9 Square specific corners (opposite of rounded top/bottom/left/right)
    // Pattern: "square top corners", "square bottom", "square left corners", "square right"
    // These preserve the existing radius on other corners and clear the shorthand to avoid React warnings
    this.commandPatterns.set(
      /^square\s+(top|bottom)\s*(?:corners)?$/i,
      (match, component) => {
        const side = match[1].toLowerCase();
        // Get existing radius from style or properties, default to 8px
        const existingRadius = this.getFlexStyle(component)?.borderRadius ||
          `${this.getFlexProps(component)?.borderRadius || 8}px`;
        if (side === 'top') {
          return {
            style: {
              borderRadius: undefined, // Clear shorthand to avoid conflicts
              borderTopLeftRadius: '0',
              borderTopRightRadius: '0',
              borderBottomLeftRadius: existingRadius,
              borderBottomRightRadius: existingRadius
            }
          };
        }
        return {
          style: {
            borderRadius: undefined,
            borderTopLeftRadius: existingRadius,
            borderTopRightRadius: existingRadius,
            borderBottomLeftRadius: '0',
            borderBottomRightRadius: '0'
          }
        };
      }
    );

    this.commandPatterns.set(
      /^square\s+(left|right)\s*(?:corners)?$/i,
      (match, component) => {
        const side = match[1].toLowerCase();
        // Get existing radius from style or properties, default to 8px
        const existingRadius = this.getFlexStyle(component)?.borderRadius ||
          `${this.getFlexProps(component)?.borderRadius || 8}px`;
        if (side === 'left') {
          return {
            style: {
              borderRadius: undefined,
              borderTopLeftRadius: '0',
              borderBottomLeftRadius: '0',
              borderTopRightRadius: existingRadius,
              borderBottomRightRadius: existingRadius
            }
          };
        }
        return {
          style: {
            borderRadius: undefined,
            borderTopRightRadius: '0',
            borderBottomRightRadius: '0',
            borderTopLeftRadius: existingRadius,
            borderBottomLeftRadius: existingRadius
          }
        };
      }
    );

    // =========================================================================
    // END PHASE 2: Border Control System
    // =========================================================================

    // =========================================================================
    // PHASE 3: Shadow & Effects System
    // =========================================================================

    // -------------------------------------------------------------------------
    // 3.1 Box Shadow Presets
    // -------------------------------------------------------------------------

    // 3.1.1 Shadow preset patterns
    // Pattern: "shadow sm", "shadow lg", "shadow 2xl"
    this.commandPatterns.set(
      /^(?:add |set )?shadow\s+(none|xs|sm|md|lg|xl|2xl)$/i,
      (match) => {
        const preset = match[1].toLowerCase();
        const shadow = SHADOW_PRESETS[preset];
        return { style: { boxShadow: shadow } };
      }
    );

    // 3.1.2 Simple "add shadow" / "drop shadow" (defaults to md)
    // Pattern: "add shadow", "drop shadow"
    this.commandPatterns.set(
      /^(?:add |set )?(?:drop[\s-]?)?shadow$/i,
      () => {
        const shadow = SHADOW_PRESETS['md'];
        return { style: { boxShadow: shadow } };
      }
    );

    // 3.1.3 "sh" shortcut pattern for power users
    // Pattern: "sh md", "sh xl", "sh none"
    this.commandPatterns.set(
      /^sh\s+(none|xs|sm|md|lg|xl|2xl)$/i,
      (match) => {
        const preset = match[1].toLowerCase();
        const shadow = SHADOW_PRESETS[preset];
        return { style: { boxShadow: shadow } };
      }
    );

    // 3.1.4 Remove shadow patterns
    // Pattern: "remove shadow", "no shadow", "delete shadow"
    this.commandPatterns.set(
      /^(?:remove|no|delete)\s+shadow$/i,
      () => {
        return { style: { boxShadow: 'none' } };
      }
    );

    // 3.1.5 "flat" pattern (remove shadow)
    // Pattern: "flat", "make it flat", "make flat"
    this.commandPatterns.set(
      /^(?:make\s+)?(?:it\s+)?flat$/i,
      () => {
        return { style: { boxShadow: 'none' } };
      }
    );

    // 3.1.6 Elevation patterns (0-5 levels)
    // Pattern: "elevation 2", "elevation 4", "set elevation 3"
    this.commandPatterns.set(
      /^(?:set\s+)?elevation\s+(\d)$/i,
      (match) => {
        const level = Math.min(parseInt(match[1], 10), 5);
        const presetKey = ELEVATION_MAP[level];
        const shadow = SHADOW_PRESETS[presetKey];
        return { style: { boxShadow: shadow } };
      }
    );

    // 3.1.7 Lift/raise/elevate patterns (defaults to lg shadow)
    // Pattern: "lift", "raise", "elevate", "lift the container"
    this.commandPatterns.set(
      /^(?:lift|raise|elevate)(?:\s+(?:the\s+)?container)?$/i,
      () => {
        const shadow = SHADOW_PRESETS['lg'];
        return { style: { boxShadow: shadow } };
      }
    );

    // -------------------------------------------------------------------------
    // 3.2 Custom Shadow Values
    // -------------------------------------------------------------------------

    // 3.2.1 Custom shadow with x/y offset only
    // Pattern: "shadow 4px 4px", "shadow 0 4"
    this.commandPatterns.set(
      /^shadow\s+(\d+)(?:px)?\s+(\d+)(?:px)?$/i,
      (match) => {
        const x = match[1];
        const y = match[2];
        const shadow = `${x}px ${y}px 8px rgba(0, 0, 0, 0.1)`;
        return { style: { boxShadow: shadow } };
      }
    );

    // 3.2.2 Custom shadow with x/y offset and blur
    // Pattern: "shadow 0 4px 8px", "shadow 2 4 12"
    this.commandPatterns.set(
      /^shadow\s+(\d+)(?:px)?\s+(\d+)(?:px)?\s+(\d+)(?:px)?$/i,
      (match) => {
        const x = match[1];
        const y = match[2];
        const blur = match[3];
        const shadow = `${x}px ${y}px ${blur}px rgba(0, 0, 0, 0.1)`;
        return { style: { boxShadow: shadow } };
      }
    );

    // 3.2.3 Custom shadow with x/y offset, blur, and color
    // Pattern: "shadow 0 4px 8px rgba(0,0,0,0.2)", "shadow 2 4 8 black"
    this.commandPatterns.set(
      /^shadow\s+(\d+)(?:px)?\s+(\d+)(?:px)?\s+(\d+)(?:px)?\s+(.+)$/i,
      (match) => {
        const x = match[1];
        const y = match[2];
        const blur = match[3];
        const colorInput = match[4].trim();
        // Check if it's an rgba/rgb value (contains parentheses)
        let color: string;
        if (colorInput.includes('(')) {
          color = colorInput;
        } else {
          // Try to parse as color name, hex, or brand color
          const brandColor = BRAND_COLORS[colorInput.toLowerCase()];
          color = brandColor || this.parseColorValue(colorInput) || 'rgba(0, 0, 0, 0.1)';
        }
        const shadow = `${x}px ${y}px ${blur}px ${color}`;
        return { style: { boxShadow: shadow } };
      }
    );

    // -------------------------------------------------------------------------
    // 3.3 Inset Shadow Patterns
    // -------------------------------------------------------------------------

    // 3.3.1 Inset shadow with optional preset
    // Pattern: "inset shadow", "inner shadow", "inset shadow lg"
    this.commandPatterns.set(
      /^(?:add\s+)?(inset|inner)\s+shadow(?:\s+(sm|md|lg))?$/i,
      (match) => {
        const preset = match[2]?.toLowerCase() || 'md';
        const shadow = INSET_SHADOW_PRESETS[preset];
        return { style: { boxShadow: shadow } };
      }
    );

    // 3.3.2 Remove inset shadow
    // Pattern: "remove inset shadow", "no inner shadow"
    this.commandPatterns.set(
      /^(?:remove|no)\s+(?:inset|inner)\s+shadow$/i,
      () => {
        return { style: { boxShadow: 'none' } };
      }
    );

    // -------------------------------------------------------------------------
    // 3.4 Component Opacity Control
    // -------------------------------------------------------------------------

    // 3.4.1 Opacity percentage pattern (overall component opacity)
    // Pattern: "opacity 50%", "set opacity 80"
    // NOTE: This is for OVERALL component opacity, distinct from Phase 1's bg opacity
    this.commandPatterns.set(
      /^(?:set\s+)?component\s+opacity\s+(\d+)%?$/i,
      (match) => {
        const opacity = parseInt(match[1], 10) / 100;
        return { style: { opacity: opacity } };
      }
    );

    // 3.4.2 Named opacity patterns
    // Pattern: "translucent", "opaque"
    this.commandPatterns.set(
      /^(?:make\s+)?(?:it\s+)?translucent$/i,
      () => {
        return { style: { opacity: 0.7 } };
      }
    );

    // 3.4.3 Reset to fully opaque (for overall component opacity)
    // Note: Phase 1's "make opaque" handles background-specific opacity
    this.commandPatterns.set(
      /^(?:set\s+)?full\s+opacity$/i,
      () => {
        return { style: { opacity: 1 } };
      }
    );

    // =========================================================================
    // END PHASE 3: Shadow & Effects System
    // =========================================================================

    // =========================================================================
    // PHASE 4: Enhanced Spacing System
    // =========================================================================

    // -------------------------------------------------------------------------
    // 4.1 Directional Padding Patterns
    // -------------------------------------------------------------------------

    // 4.1.1 Vertical/Horizontal padding
    // Pattern: "padding vertical 20", "vertical padding 16", "padding-vertical 10"
    // NOTE: Must clear shorthand padding to avoid CSS conflicts
    this.commandPatterns.set(
      /^(?:set |add )?(?:padding[\s-]?vertical|vertical[\s-]?padding)\s+(\d+)(?:px)?$/i,
      (match) => {
        const value = `${match[1]}px`;
        return {
          style: {
            padding: undefined, // Clear shorthand to avoid conflicts
            paddingTop: value,
            paddingBottom: value
          }
        };
      }
    );

    // Pattern: "padding horizontal 20", "horizontal padding 16", "padding-horizontal 10"
    // NOTE: Must clear shorthand padding to avoid CSS conflicts
    this.commandPatterns.set(
      /^(?:set |add )?(?:padding[\s-]?horizontal|horizontal[\s-]?padding)\s+(\d+)(?:px)?$/i,
      (match) => {
        const value = `${match[1]}px`;
        return {
          style: {
            padding: undefined, // Clear shorthand to avoid conflicts
            paddingLeft: value,
            paddingRight: value
          }
        };
      }
    );

    // 4.1.2 Individual side padding (padding-first syntax)
    // Pattern: "padding top 10", "padding-top 20", "padding left 16px"
    // NOTE: Must clear shorthand padding to avoid CSS conflicts
    this.commandPatterns.set(
      /^(?:set |add )?padding[\s-]?(top|right|bottom|left)\s+(\d+)(?:px)?$/i,
      (match) => {
        const side = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
        const value = `${match[2]}px`;
        return { style: { padding: undefined, [`padding${side}`]: value } };
      }
    );

    // 4.1.2b Individual side padding (side-first syntax)
    // Pattern: "top padding 10", "left padding 24px", "right padding 8"
    // NOTE: Must clear shorthand padding to avoid CSS conflicts
    this.commandPatterns.set(
      /^(?:set |add )?(top|right|bottom|left)[\s-]?padding\s+(\d+)(?:px)?$/i,
      (match) => {
        const side = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
        const value = `${match[2]}px`;
        return { style: { padding: undefined, [`padding${side}`]: value } };
      }
    );

    // 4.1.3 Two-value padding shorthand (vertical horizontal)
    // Pattern: "padding 10 20", "set padding 16 32"
    // NOTE: Must clear shorthand padding to avoid CSS conflicts
    this.commandPatterns.set(
      /^(?:set |add )?padding\s+(\d+)(?:px)?\s+(\d+)(?:px)?$/i,
      (match) => {
        const vertical = `${match[1]}px`;
        const horizontal = `${match[2]}px`;
        return {
          style: {
            padding: undefined, // Clear shorthand to avoid conflicts
            paddingTop: vertical,
            paddingBottom: vertical,
            paddingLeft: horizontal,
            paddingRight: horizontal
          }
        };
      }
    );

    // 4.1.4 Four-value padding shorthand (top right bottom left)
    // Pattern: "padding 10 20 10 20", "set padding 8 16 8 16"
    // NOTE: Must clear shorthand padding to avoid CSS conflicts
    this.commandPatterns.set(
      /^(?:set |add )?padding\s+(\d+)(?:px)?\s+(\d+)(?:px)?\s+(\d+)(?:px)?\s+(\d+)(?:px)?$/i,
      (match) => {
        const top = `${match[1]}px`;
        const right = `${match[2]}px`;
        const bottom = `${match[3]}px`;
        const left = `${match[4]}px`;
        return {
          style: {
            padding: undefined, // Clear shorthand to avoid conflicts
            paddingTop: top,
            paddingRight: right,
            paddingBottom: bottom,
            paddingLeft: left
          }
        };
      }
    );

    // 4.1.5 Remove padding patterns
    // Pattern: "no padding", "remove padding", "zero padding", "clear padding"
    // NOTE: Use individual properties only to avoid shorthand/longhand conflicts
    this.commandPatterns.set(
      /^(?:no|remove|zero|clear)\s+padding$/i,
      () => {
        return {
          properties: { padding: 0 },
          style: {
            padding: undefined, // Clear shorthand to avoid conflicts
            paddingTop: '0px',
            paddingRight: '0px',
            paddingBottom: '0px',
            paddingLeft: '0px'
          }
        };
      }
    );

    // 4.1.5b "pd" shortcut pattern for power users
    // Pattern: "pd 10", "pd 10 20", "pd 10 20 10 20"
    // NOTE: Must set BOTH properties.padding AND style.padding for FlexContainer to apply
    // NOTE: Must clear conflicting properties to avoid CSS shorthand/longhand conflicts
    this.commandPatterns.set(
      /^pd\s+(\d+)(?:px)?(?:\s+(\d+)(?:px)?)?(?:\s+(\d+)(?:px)?)?(?:\s+(\d+)(?:px)?)?$/i,
      (match) => {
        const values = [match[1], match[2], match[3], match[4]].filter(Boolean);

        if (values.length === 1) {
          // Uniform padding - use shorthand, clear individual
          const value = parseInt(values[0], 10);
          return {
            properties: { padding: value },
            style: {
              padding: `${value}px`,
              paddingTop: undefined,
              paddingRight: undefined,
              paddingBottom: undefined,
              paddingLeft: undefined
            }
          };
        } else if (values.length === 2) {
          // Two-value shorthand - use individual, clear shorthand
          return {
            style: {
              padding: undefined,
              paddingTop: `${values[0]}px`,
              paddingBottom: `${values[0]}px`,
              paddingLeft: `${values[1]}px`,
              paddingRight: `${values[1]}px`
            }
          };
        } else if (values.length === 4) {
          // Four-value shorthand - use individual, clear shorthand
          return {
            style: {
              padding: undefined,
              paddingTop: `${values[0]}px`,
              paddingRight: `${values[1]}px`,
              paddingBottom: `${values[2]}px`,
              paddingLeft: `${values[3]}px`
            }
          };
        }
        return null;
      }
    );

    // -------------------------------------------------------------------------
    // 4.2 Margin Control Patterns
    // -------------------------------------------------------------------------

    // 4.2.1 Uniform margin
    // Pattern: "margin 20", "set margin 16px", "add margin 10"
    this.commandPatterns.set(
      /^(?:set |add )?margin\s+(\d+)(?:px)?$/i,
      (match) => {
        const value = `${match[1]}px`;
        return { style: { margin: value } };
      }
    );

    // 4.2.2 Auto margins for centering
    // Pattern: "margin auto", "center with margin", "auto margin"
    this.commandPatterns.set(
      /^(?:margin\s+auto|auto\s+margin|center\s+with\s+margin)$/i,
      () => {
        return { style: { margin: 'auto' } };
      }
    );

    // 4.2.2b Horizontal auto margin
    // Pattern: "margin horizontal auto", "horizontal margin auto", "margin x auto"
    this.commandPatterns.set(
      /^(?:margin\s+(?:horizontal|x)\s+auto|(?:horizontal|x)\s+margin\s+auto)$/i,
      () => {
        return {
          style: {
            marginLeft: 'auto',
            marginRight: 'auto'
          }
        };
      }
    );

    // 4.2.2c Vertical auto margin
    // Pattern: "margin vertical auto", "vertical margin auto", "margin y auto"
    this.commandPatterns.set(
      /^(?:margin\s+(?:vertical|y)\s+auto|(?:vertical|y)\s+margin\s+auto)$/i,
      () => {
        return {
          style: {
            marginTop: 'auto',
            marginBottom: 'auto'
          }
        };
      }
    );

    // 4.2.3 Directional margins (margin-first syntax)
    // Pattern: "margin top 10", "margin-left 20", "margin right 16px"
    // NOTE: Clears opposite side to prevent conflicts with previous 'auto' margins
    this.commandPatterns.set(
      /^(?:set |add )?margin[\s-]?(top|right|bottom|left)\s+(\d+)(?:px)?$/i,
      (match) => {
        const side = match[1].toLowerCase();
        const value = `${match[2]}px`;

        // Build style with the specified side and clear the opposite to prevent auto conflicts
        const styleMap: Record<string, string> = {
          top: 'Top',
          bottom: 'Bottom',
          left: 'Left',
          right: 'Right'
        };
        const oppositeMap: Record<string, string> = {
          top: 'Bottom',
          bottom: 'Top',
          left: 'Right',
          right: 'Left'
        };

        return {
          style: {
            [`margin${styleMap[side]}`]: value,
            [`margin${oppositeMap[side]}`]: '0'
          }
        };
      }
    );

    // 4.2.3b Directional margins (side-first syntax)
    // Pattern: "top margin 10", "left margin 24px", "bottom margin 8"
    // NOTE: Clears opposite side to prevent conflicts with previous 'auto' margins
    this.commandPatterns.set(
      /^(?:set |add )?(top|right|bottom|left)[\s-]?margin\s+(\d+)(?:px)?$/i,
      (match) => {
        const side = match[1].toLowerCase();
        const value = `${match[2]}px`;

        // Build style with the specified side and clear the opposite to prevent auto conflicts
        const styleMap: Record<string, string> = {
          top: 'Top',
          bottom: 'Bottom',
          left: 'Left',
          right: 'Right'
        };
        const oppositeMap: Record<string, string> = {
          top: 'Bottom',
          bottom: 'Top',
          left: 'Right',
          right: 'Left'
        };

        return {
          style: {
            [`margin${styleMap[side]}`]: value,
            [`margin${oppositeMap[side]}`]: '0'
          }
        };
      }
    );

    // 4.2.4 Vertical/Horizontal margins
    // Pattern: "margin vertical 20", "vertical margin 16"
    this.commandPatterns.set(
      /^(?:set |add )?(?:margin[\s-]?vertical|vertical[\s-]?margin)\s+(\d+)(?:px)?$/i,
      (match) => {
        const value = `${match[1]}px`;
        return {
          style: {
            marginTop: value,
            marginBottom: value
          }
        };
      }
    );

    // Pattern: "margin horizontal 20", "horizontal margin 16"
    this.commandPatterns.set(
      /^(?:set |add )?(?:margin[\s-]?horizontal|horizontal[\s-]?margin)\s+(\d+)(?:px)?$/i,
      (match) => {
        const value = `${match[1]}px`;
        return {
          style: {
            marginLeft: value,
            marginRight: value
          }
        };
      }
    );

    // 4.2.5 Two-value margin shorthand (vertical horizontal)
    // Pattern: "margin 10 20", "set margin 16 32"
    this.commandPatterns.set(
      /^(?:set |add )?margin\s+(\d+)(?:px)?\s+(\d+)(?:px)?$/i,
      (match) => {
        const vertical = `${match[1]}px`;
        const horizontal = `${match[2]}px`;
        return {
          style: {
            marginTop: vertical,
            marginBottom: vertical,
            marginLeft: horizontal,
            marginRight: horizontal
          }
        };
      }
    );

    // 4.2.6 Four-value margin shorthand (top right bottom left)
    // Pattern: "margin 10 20 10 20", "set margin 8 16 8 16"
    this.commandPatterns.set(
      /^(?:set |add )?margin\s+(\d+)(?:px)?\s+(\d+)(?:px)?\s+(\d+)(?:px)?\s+(\d+)(?:px)?$/i,
      (match) => {
        const top = `${match[1]}px`;
        const right = `${match[2]}px`;
        const bottom = `${match[3]}px`;
        const left = `${match[4]}px`;
        return {
          style: {
            marginTop: top,
            marginRight: right,
            marginBottom: bottom,
            marginLeft: left
          }
        };
      }
    );

    // 4.2.7 Remove margin patterns
    // Pattern: "no margin", "remove margin", "zero margin", "clear margin"
    this.commandPatterns.set(
      /^(?:no|remove|zero|clear)\s+margin$/i,
      () => {
        return {
          style: {
            margin: '0px',
            marginTop: '0px',
            marginRight: '0px',
            marginBottom: '0px',
            marginLeft: '0px'
          }
        };
      }
    );

    // 4.2.7b "mg" shortcut pattern for power users
    // Pattern: "mg 10", "mg 10 20", "mg 10 20 10 20", "mg auto"
    this.commandPatterns.set(
      /^mg\s+(?:auto|(\d+)(?:px)?(?:\s+(\d+)(?:px)?)?(?:\s+(\d+)(?:px)?)?(?:\s+(\d+)(?:px)?)?)$/i,
      (match) => {

        // Check for auto
        if (match[0].toLowerCase().includes('auto')) {
          return { style: { margin: 'auto' } };
        }

        const values = [match[1], match[2], match[3], match[4]].filter(Boolean);

        if (values.length === 1) {
          // Uniform margin
          return { style: { margin: `${values[0]}px` } };
        } else if (values.length === 2) {
          // Two-value shorthand
          return {
            style: {
              marginTop: `${values[0]}px`,
              marginBottom: `${values[0]}px`,
              marginLeft: `${values[1]}px`,
              marginRight: `${values[1]}px`
            }
          };
        } else if (values.length === 4) {
          // Four-value shorthand
          return {
            style: {
              marginTop: `${values[0]}px`,
              marginRight: `${values[1]}px`,
              marginBottom: `${values[2]}px`,
              marginLeft: `${values[3]}px`
            }
          };
        }
        return null;
      }
    );

    // -------------------------------------------------------------------------
    // 4.3 Row/Column Gap Patterns
    // -------------------------------------------------------------------------

    // 4.3.1 Row gap patterns
    // Pattern: "row gap 10", "row-gap 16", "vertical gap 20"
    // NOTE: Must set BOTH properties AND style for FlexContainer to apply
    this.commandPatterns.set(
      /^(?:set |add )?(?:row[\s-]?gap|vertical[\s-]?gap)\s+(\d+)(?:px)?$/i,
      (match) => {
        const value = parseInt(match[1], 10);
        return { properties: { rowGap: value }, style: { rowGap: `${value}px` } };
      }
    );

    // 4.3.2 Column gap patterns
    // Pattern: "column gap 10", "column-gap 16", "horizontal gap 20", "col gap 8"
    // NOTE: Must set BOTH properties AND style for FlexContainer to apply
    this.commandPatterns.set(
      /^(?:set |add )?(?:column[\s-]?gap|col[\s-]?gap|horizontal[\s-]?gap)\s+(\d+)(?:px)?$/i,
      (match) => {
        const value = parseInt(match[1], 10);
        return { properties: { columnGap: value }, style: { columnGap: `${value}px` } };
      }
    );

    // 4.3.3 Separate row and column gaps
    // Pattern: "gap 10 20" (row column), "gaps 16 8"
    // NOTE: Must set BOTH properties AND style for FlexContainer to apply
    this.commandPatterns.set(
      /^(?:set |add )?gaps?\s+(\d+)(?:px)?\s+(\d+)(?:px)?$/i,
      (match) => {
        const rowGap = parseInt(match[1], 10);
        const columnGap = parseInt(match[2], 10);
        return {
          properties: { rowGap, columnGap },
          style: { rowGap: `${rowGap}px`, columnGap: `${columnGap}px` }
        };
      }
    );

    // 4.3.4 Remove row gap
    // Pattern: "no row gap", "remove row gap", "remove vertical gap"
    this.commandPatterns.set(
      /^(?:no|remove|zero|clear)\s+(?:row|vertical)[\s-]?gap$/i,
      () => {
        return { properties: { rowGap: 0 }, style: { rowGap: '0px' } };
      }
    );

    // 4.3.5 Remove column gap
    // Pattern: "no column gap", "remove column gap", "remove horizontal gap"
    this.commandPatterns.set(
      /^(?:no|remove|zero|clear)\s+(?:column|col|horizontal)[\s-]?gap$/i,
      () => {
        return { properties: { columnGap: 0 }, style: { columnGap: '0px' } };
      }
    );

    // =========================================================================
    // END PHASE 4: Enhanced Spacing System
    // =========================================================================

    // Legacy border radius patterns (keep for backwards compatibility)
    // Use style for consistency with Phase 2 patterns
    // Clear individual corners to avoid conflicts with existing values
    this.commandPatterns.set(
      /^(?:add |set )?rounded\s+corners$/i,
      (_match, _component) => ({
        style: {
          borderRadius: '8px',
          borderTopLeftRadius: undefined,
          borderTopRightRadius: undefined,
          borderBottomLeftRadius: undefined,
          borderBottomRightRadius: undefined
        }
      })
    );

    this.commandPatterns.set(
      /^(?:add |set )?border[\s-]?radius\s+(\d+)(?:px)?$/i,
      (match, _component) => ({
        style: {
          borderRadius: `${match[1]}px`,
          borderTopLeftRadius: undefined,
          borderTopRightRadius: undefined,
          borderBottomLeftRadius: undefined,
          borderBottomRightRadius: undefined
        }
      })
    );

    // Size patterns with viewport units - IMPORTANT: Use 'size' not 'style' for dimensions
    this.commandPatterns.set(
      /^(?:set |make )?height\s+(\d+)vh$/i,
      (match, _component) => ({ size: { height: `${match[1]}vh` } })
    );

    this.commandPatterns.set(
      /^(?:set |make )?width\s+(\d+)vw$/i,
      (match, _component) => ({ size: { width: `${match[1]}vw` } })
    );

    this.commandPatterns.set(
      /^(?:set |make )?height\s+(\d+)%$/i,
      (match, _component) => ({ size: { height: `${match[1]}%` } })
    );

    this.commandPatterns.set(
      /^(?:set |make )?width\s+(\d+)%$/i,
      (match, _component) => ({ size: { width: `${match[1]}%` } })
    );

    // Min/max size patterns
    this.commandPatterns.set(
      /^(?:set |make )?min[\s-]?height\s+(\d+)(?:px|vh|%)?$/i,
      (match, _component) => {
        const unit = match[0].includes('vh') ? 'vh' : match[0].includes('%') ? '%' : 'px';
        return { style: { minHeight: `${match[1]}${unit}` } };
      }
    );

    this.commandPatterns.set(
      /^(?:set |make )?max[\s-]?height\s+(\d+)(?:px|vh|%)?$/i,
      (match, _component) => {
        const unit = match[0].includes('vh') ? 'vh' : match[0].includes('%') ? '%' : 'px';
        return { style: { maxHeight: `${match[1]}${unit}` } };
      }
    );

    this.commandPatterns.set(
      /^(?:set |make )?min[\s-]?width\s+(\d+)(?:px|vw|%)?$/i,
      (match, _component) => {
        const unit = match[0].includes('vw') ? 'vw' : match[0].includes('%') ? '%' : 'px';
        return { style: { minWidth: `${match[1]}${unit}` } };
      }
    );

    this.commandPatterns.set(
      /^(?:set |make )?max[\s-]?width\s+(\d+)(?:px|vw|%)?$/i,
      (match, _component) => {
        const unit = match[0].includes('vw') ? 'vw' : match[0].includes('%') ? '%' : 'px';
        return { style: { maxWidth: `${match[1]}${unit}` } };
      }
    );

    // Remove min-height patterns (to fix visual "padding" from fixed min-height)
    this.commandPatterns.set(
      /^(?:no|remove|clear|reset)\s*min[\s-]?height$/i,
      () => {
        return {
          style: { minHeight: 'auto' },
          properties: { minHeight: undefined }
        };
      }
    );

    // Auto height pattern (content-based sizing)
    this.commandPatterns.set(
      /^(?:auto|content|fit)[\s-]?height$/i,
      () => {
        return {
          style: { height: 'auto', minHeight: 'auto' },
          properties: { minHeight: undefined },
          size: { height: 'auto' }
        };
      }
    );

    // Fit content height pattern
    this.commandPatterns.set(
      /^(?:fit|shrink)[\s-]?(?:to[\s-]?)?content$/i,
      () => {
        return {
          style: { height: 'fit-content', minHeight: 'auto' },
          properties: { minHeight: undefined },
          size: { height: 'fit-content' }
        };
      }
    );

    // Overflow patterns
    this.commandPatterns.set(
      /^(?:set |make )?scrollable$/i,
      (_match, _component) => ({ style: { overflow: 'auto' } })
    );

    this.commandPatterns.set(
      /^(?:hide |clip )?overflow$/i,
      (_match, _component) => ({ style: { overflow: 'hidden' } })
    );

    this.commandPatterns.set(
      /^(?:show )?scroll(?:bars)?$/i,
      (_match, _component) => ({ style: { overflow: 'scroll' } })
    );

    // =========================================================================
    // REFRESH BADGE: Live Update Badge Patterns
    // =========================================================================
    // "make this a refresh badge", "enable refresh", "make live update badge"
    this.commandPatterns.set(
      /^(?:make|set|configure|enable)\s+(?:this\s+)?(?:a\s+)?(?:refresh|live\s*update|last\s*updated)\s*(?:badge|button)?$/i,
      (_match, component) => this.handleRefreshBadge(component)
    );
    // "show last updated", "show live timestamp"
    this.commandPatterns.set(
      /^(?:show|display|enable)\s+(?:live\s+)?(?:last\s*)?(?:updated?|timestamp)$/i,
      (_match, component) => this.handleRefreshBadge(component)
    );

    // =========================================================================
    // PHASE 5: Responsive Styling
    // =========================================================================
    // Breakpoint-specific commands that allow users to apply styles for
    // specific device sizes (mobile, tablet, desktop) through natural language.
    //
    // IMPORTANT: These patterns must be added LAST because they delegate to
    // other patterns via parseSubCommand. This ensures the breakpoint prefix
    // is stripped before matching against regular patterns.
    // =========================================================================

    // -------------------------------------------------------------------------
    // 5.1 Breakpoint-Specific Commands
    // -------------------------------------------------------------------------

    // 5.1.1 "on mobile/tablet/desktop" prefix syntax
    // Pattern: "on mobile bg red", "on tablet padding 20", "on desktop shadow lg"
    this.commandPatterns.set(
      /^on\s+(mobile|tablet|desktop)\s+(.+)$/i,
      (match, component) => this.handleBreakpointCommand(match[1], match[2], component)
    );

    // 5.1.2 Colon syntax for breakpoints
    // Pattern: "mobile: bg red", "tablet: direction row", "desktop: gap 20"
    this.commandPatterns.set(
      /^(mobile|tablet|desktop):\s*(.+)$/i,
      (match, component) => this.handleBreakpointCommand(match[1], match[2], component)
    );

    // 5.1.3 "only on" syntax
    // Pattern: "only on mobile bg red", "only on tablet hide"
    this.commandPatterns.set(
      /^only\s+on\s+(mobile|tablet|desktop)\s+(.+)$/i,
      (match, component) => this.handleBreakpointCommand(match[1], match[2], component)
    );

    // 5.1.4 Simple breakpoint prefix (no colon, no "on")
    // Pattern: "mobile bg red", "tablet padding 20", "desktop shadow lg"
    // NOTE: More restrictive - sub-command must start with known command words
    this.commandPatterns.set(
      /^(mobile|tablet|desktop)\s+(bg|background|padding|margin|gap|border|shadow|radius|rounded|direction|align|justify|center|hide|show|flex|wrap|transparent|opaque|gradient)\b(.*)$/i,
      (match, component) => {
        const subCommand = match[2] + (match[3] || '');
        return this.handleBreakpointCommand(match[1], subCommand.trim(), component);
      }
    );

    // =========================================================================
    // PHASE 6: Natural Language Refinements
    // =========================================================================
    // These patterns enable more natural, conversational interaction with the
    // AI chat system. They support relative value adjustments, polite forms,
    // and question-based commands.
    // =========================================================================

    // -------------------------------------------------------------------------
    // 6.1 Relative Value Commands
    // -------------------------------------------------------------------------

    // 6.1.1 Darker/Lighter background
    // Pattern: "make it darker", "darker", "lighter background", "lighter", "darker background"
    this.commandPatterns.set(
      /^(?:make\s+)?(?:it\s+|the\s+)?(?:(?:background\s+|bg\s+)?(darker|lighter)|(?:(darker|lighter)\s+(?:background|bg)))$/i,
      (match, component) => {
        const colorWord = match[1] || match[2];
        const adjustment = colorWord.toLowerCase() === 'darker' ? -30 : 30;
        const currentBg = this.getCurrentBackgroundColor(component);
        const newColor = this.adjustColorBrightness(currentBg, adjustment);
        return { properties: { backgroundColor: newColor }, style: { background: 'none' } };
      }
    );

    // 6.1.2 More/Less for numeric properties
    // Pattern: "more padding", "less gap", "more margin", "less radius", "more rounded"
    // Uses additive increment (+8px/-8px) for intuitive behavior
    this.commandPatterns.set(
      /^(more|less|bigger|smaller)\s+(padding|gap|margin|radius|shadow|spacing|rounded|border[\s-]?radius)$/i,
      (match, component) => {
        const modifier = match[1].toLowerCase();
        const property = match[2].toLowerCase().replace(/[\s-]/g, '');
        const isMore = modifier === 'more' || modifier === 'bigger';
        const increment = isMore ? 8 : -8; // Additive change in pixels


        // Handle shadow specially (adjust preset level)
        if (property === 'shadow') {
          return this.adjustShadowLevel(component, isMore);
        }

        // Handle border radius (includes "rounded")
        if (property === 'radius' || property === 'borderradius' || property === 'rounded') {
          return this.adjustRadiusLevel(component, isMore);
        }

        // Get current value from component (check style first, then properties)
        const props = this.getFlexProps(component);
        const style = this.getFlexStyle(component);

        // Map property names and get current values with proper unit conversion
        // Check style first, then properties, then use fallback
        if (property === 'padding') {
          const currentPx = this.getCurrentSpacingPixels(component, style, props, 'padding', 16);
          const newValue = Math.max(0, currentPx + increment);
          return { properties: { padding: newValue }, style: { padding: `${newValue}px` } };
        } else if (property === 'gap' || property === 'spacing') {
          const currentPx = this.getCurrentSpacingPixels(component, style, props, 'gap', 16);
          const newValue = Math.max(0, currentPx + increment);
          return { properties: { gap: newValue }, style: { gap: `${newValue}px` } };
        } else if (property === 'margin') {
          const currentPx = this.getCurrentSpacingPixels(component, style, props, 'margin', 16);
          const newValue = Math.max(0, currentPx + increment);
          return { style: { margin: `${newValue}px` } };
        }

        return null;
      }
    );

    this.commandPatterns.set(
      /^(?:make\s+)?(?:this\s+)?(?:selected\s+)?component\s+(?:more\s+)?(compact|roomy|roomier)$/i,
      (match, component) => this.adjustContainerDensity(component, match[1].toLowerCase() !== 'compact')
    );

    this.commandPatterns.set(
      /^(?:make\s+)?(?:it\s+)?(?:more\s+)?(compact|roomy|roomier)$/i,
      (match, component) => this.adjustContainerDensity(component, match[1].toLowerCase() !== 'compact')
    );

    // 6.1.3 Double/Half commands
    // Pattern: "double the padding", "half the gap", "double padding", "half gap"
    // Uses multiplicative change (2x/0.5x) with proper unit parsing
    this.commandPatterns.set(
      /^(double|half)\s+(?:the\s+)?(padding|gap|margin|spacing|radius|border[\s-]?radius)$/i,
      (match, component) => {
        const modifier = match[1].toLowerCase();
        const property = match[2].toLowerCase().replace(/[\s-]/g, '');
        const multiplier = modifier === 'double' ? 2 : 0.5;


        const props = this.getFlexProps(component);
        const style = this.getFlexStyle(component);

        // Check style first, then properties, then use fallback
        if (property === 'padding') {
          const currentPx = this.getCurrentSpacingPixels(component, style, props, 'padding', 16);
          const newValue = Math.max(0, Math.round(currentPx * multiplier));
          return { properties: { padding: newValue }, style: { padding: `${newValue}px` } };
        } else if (property === 'gap' || property === 'spacing') {
          const currentPx = this.getCurrentSpacingPixels(component, style, props, 'gap', 16);
          const newValue = Math.max(0, Math.round(currentPx * multiplier));
          return { properties: { gap: newValue }, style: { gap: `${newValue}px` } };
        } else if (property === 'margin') {
          const currentPx = this.getCurrentSpacingPixels(component, style, props, 'margin', 16);
          const newValue = Math.max(0, Math.round(currentPx * multiplier));
          return { style: { margin: `${newValue}px` } };
        } else if (property === 'radius' || property === 'borderradius') {
          const currentPx = this.getCurrentSpacingPixels(component, style, props, 'borderRadius', 8);
          const newValue = Math.max(0, Math.round(currentPx * multiplier));
          return {
            style: {
              borderRadius: `${newValue}px`,
              borderTopLeftRadius: undefined,
              borderTopRightRadius: undefined,
              borderBottomLeftRadius: undefined,
              borderBottomRightRadius: undefined
            }
          };
        }

        return null;
      }
    );

    // 6.1.4 Increase/Decrease commands (alternative syntax)
    // Pattern: "increase padding", "decrease gap", "increase shadow"
    // Uses additive increment (+8px/-8px) for intuitive behavior
    this.commandPatterns.set(
      /^(increase|decrease)\s+(?:the\s+)?(padding|gap|margin|spacing|radius|shadow|border[\s-]?radius)$/i,
      (match, component) => {
        const modifier = match[1].toLowerCase();
        const property = match[2].toLowerCase().replace(/[\s-]/g, '');
        const isIncrease = modifier === 'increase';
        const increment = isIncrease ? 8 : -8; // Additive change in pixels


        // Handle shadow specially (adjust preset level)
        if (property === 'shadow') {
          return this.adjustShadowLevel(component, isIncrease);
        }

        // Handle border radius (cycles through presets)
        if (property === 'radius' || property === 'borderradius') {
          return this.adjustRadiusLevel(component, isIncrease);
        }

        // Use proper unit parsing for spacing properties
        // Check style first, then properties, then use fallback
        const props = this.getFlexProps(component);
        const style = this.getFlexStyle(component);

        if (property === 'padding') {
          const currentPx = this.getCurrentSpacingPixels(component, style, props, 'padding', 16);
          const newValue = Math.max(0, currentPx + increment);
          return { properties: { padding: newValue }, style: { padding: `${newValue}px` } };
        } else if (property === 'gap' || property === 'spacing') {
          const currentPx = this.getCurrentSpacingPixels(component, style, props, 'gap', 16);
          const newValue = Math.max(0, currentPx + increment);
          return { properties: { gap: newValue }, style: { gap: `${newValue}px` } };
        } else if (property === 'margin') {
          const currentPx = this.getCurrentSpacingPixels(component, style, props, 'margin', 16);
          const newValue = Math.max(0, currentPx + increment);
          return { style: { margin: `${newValue}px` } };
        }

        return null;
      }
    );

    this.commandPatterns.set(
      /^(increase|decrease|reduce|lessen)\s+(?:the\s+)?vertical\s+(padding|spacing)$/i,
      (match, component) => {
        const modifier = match[1].toLowerCase();
        const isIncrease = modifier === 'increase';
        const increment = isIncrease ? 8 : -8;

        const props = this.getFlexProps(component);
        const style = this.getFlexStyle(component);

        let currentTop = 16;
        let currentBottom = 16;

        const sharedPadding = style.padding ?? props.padding;
        if (sharedPadding !== undefined && sharedPadding !== null && sharedPadding !== '') {
          const sharedPx = this.parseSpacingToPixels(this.getSpacingValue(sharedPadding) ?? 0, 16);
          currentTop = sharedPx;
          currentBottom = sharedPx;
        }

        if (style.paddingTop !== undefined && style.paddingTop !== null && style.paddingTop !== '') {
          currentTop = this.parseSpacingToPixels(this.getSpacingValue(style.paddingTop) ?? 0, currentTop);
        }
        if (style.paddingBottom !== undefined && style.paddingBottom !== null && style.paddingBottom !== '') {
          currentBottom = this.parseSpacingToPixels(this.getSpacingValue(style.paddingBottom) ?? 0, currentBottom);
        }

        const nextTop = Math.max(0, currentTop + increment);
        const nextBottom = Math.max(0, currentBottom + increment);

        return {
          style: {
            padding: undefined,
            paddingTop: `${nextTop}px`,
            paddingBottom: `${nextBottom}px`,
          }
        };
      }
    );

    // -------------------------------------------------------------------------
    // 6.2 Conversational Commands
    // -------------------------------------------------------------------------
    // These patterns strip polite prefixes and question forms, then re-parse
    // the core command. They must be checked BEFORE other patterns.
    // -------------------------------------------------------------------------

    // 6.2.1 Question form stripping
    // Pattern: "can you make it blue?", "could you add shadow?", "would you center it?"
    this.commandPatterns.set(
      /^(?:can you |could you |would you |will you )(.+?)\??$/i,
      (match, component) => {
        const coreCommand = match[1].trim();
        return this.parseSubCommand(coreCommand, component);
      }
    );

    // 6.2.2 Please prefix stripping
    // Pattern: "please add shadow", "please make it blue", "please center everything"
    this.commandPatterns.set(
      /^please\s+(.+)$/i,
      (match, component) => {
        const coreCommand = match[1].trim();
        return this.parseSubCommand(coreCommand, component);
      }
    );

    // 6.2.3 "I want/need/think" prefix stripping
    // Pattern: "I want a bigger radius", "I need more padding", "I think it needs more shadow"
    this.commandPatterns.set(
      /^i\s+(?:want|need|would like|think (?:it )?needs?)\s+(.+)$/i,
      (match, component) => {
        let coreCommand = match[1].trim();
        // Remove leading "a" or "an" if present
        coreCommand = coreCommand.replace(/^an?\s+/i, '');
        return this.parseSubCommand(coreCommand, component);
      }
    );

    // 6.2.4 "Let's" prefix stripping
    // Pattern: "let's add shadow", "let's make it blue"
    this.commandPatterns.set(
      /^let'?s\s+(.+)$/i,
      (match, component) => {
        const coreCommand = match[1].trim();
        return this.parseSubCommand(coreCommand, component);
      }
    );

    // 6.2.5 "Maybe/Try" suggestion prefix
    // Pattern: "maybe add shadow", "try making it blue"
    this.commandPatterns.set(
      /^(?:maybe|try)\s+(?:to\s+)?(?:adding?|making?|setting?)?\s*(.+)$/i,
      (match, component) => {
        let coreCommand = match[1].trim();
        // Handle "making it X" -> "make it X"
        coreCommand = coreCommand.replace(/^it\s+/, 'make it ');
        return this.parseSubCommand(coreCommand, component);
      }
    );

    // =========================================================================
    // PHASE 7: Advanced Features
    // =========================================================================

    // -------------------------------------------------------------------------
    // 7.2 Preset/Theme Commands
    // -------------------------------------------------------------------------

    // 7.2.0 List presets help command
    // Pattern: "list presets", "what presets", "available presets", "show presets"
    this.commandPatterns.set(
      /^(?:list|show|what(?:\s+are)?(?:\s+the)?|available|get)\s+(?:style\s+)?presets\??$/i,
      (_match, _component) => {

        // Return info marker - no actual style changes
        return {
          properties: { _presetInfo: true }
        };
      }
    );

    // 7.2.1 Card preset
    // Pattern: "make it a card", "card style", "card preset"
    this.commandPatterns.set(
      /^(?:make\s+(?:it\s+)?(?:a\s+)?|apply\s+)?card(?:\s+(?:style|preset))?$/i,
      (_match, _component) => {
        return STYLE_PRESETS.card;
      }
    );

    // 7.2.2 Panel preset
    // Pattern: "panel style", "make it a panel", "panel"
    this.commandPatterns.set(
      /^(?:make\s+(?:it\s+)?(?:a\s+)?|apply\s+)?panel(?:\s+(?:style|preset))?$/i,
      (_match, _component) => {
        return STYLE_PRESETS.panel;
      }
    );

    // 7.2.3 Outlined preset
    // Pattern: "outlined", "outlined style", "make it outlined"
    this.commandPatterns.set(
      /^(?:make\s+(?:it\s+)?)?outlined(?:\s+(?:style|preset))?$/i,
      (_match, _component) => {
        return STYLE_PRESETS.outlined;
      }
    );

    // 7.2.4 Elevated preset
    // Pattern: "make it elevated", "elevated style", "elevated"
    this.commandPatterns.set(
      /^(?:make\s+(?:it\s+)?)?elevated(?:\s+(?:style|preset))?$/i,
      (_match, _component) => {
        return STYLE_PRESETS.elevated;
      }
    );

    // -------------------------------------------------------------------------
    // 7.1 Style Copying Commands
    // -------------------------------------------------------------------------

    // 7.1.1 "copy style from <component>" pattern
    // Pattern: "copy style from header", "copy the style from sidebar", "copy styles from the card"
    this.commandPatterns.set(
      /^copy\s+(?:the\s+)?styles?\s+(?:from|of)\s+(?:the\s+)?(.+)$/i,
      (match, component) => {
        const sourceRef = match[1].trim();
        return this.handleStyleCopy(sourceRef, component, 'all');
      }
    );

    // 7.1.2 "match X's background/colors" pattern
    // Pattern: "match the header's background", "match sidebar's colors", "match card's background"
    this.commandPatterns.set(
      /^match\s+(?:the\s+)?(.+?)(?:'s|s')\s+(background|colors?|border|shadow|spacing|typography|font)$/i,
      (match, component) => {
        const sourceRef = match[1].trim();
        const propertyType = match[2].toLowerCase();
        const scope = this.mapPropertyToScope(propertyType);
        return this.handleStyleCopy(sourceRef, component, scope);
      }
    );

    // 7.1.3 "same border/background as <component>" pattern
    // Pattern: "same border as sidebar", "same background as the card", "same style as header"
    this.commandPatterns.set(
      /^same\s+(border|background|colors?|shadow|spacing|typography|font|style)\s+as\s+(?:the\s+)?(.+)$/i,
      (match, component) => {
        const propertyType = match[1].toLowerCase();
        const sourceRef = match[2].trim();
        const scope = this.mapPropertyToScope(propertyType);
        return this.handleStyleCopy(sourceRef, component, scope);
      }
    );

    // 7.1.4 "use X's style" pattern
    // Pattern: "use the header's style", "use card's colors"
    this.commandPatterns.set(
      /^use\s+(?:the\s+)?(.+?)(?:'s|s')\s+(style|colors?|background|border|shadow)$/i,
      (match, component) => {
        const sourceRef = match[1].trim();
        const propertyType = match[2].toLowerCase();
        const scope = this.mapPropertyToScope(propertyType);
        return this.handleStyleCopy(sourceRef, component, scope);
      }
    );

    // 7.1.5 "look like <component>" pattern
    // Pattern: "look like the header", "look like sidebar"
    this.commandPatterns.set(
      /^(?:make\s+(?:it|this)\s+)?look\s+like\s+(?:the\s+)?(.+)$/i,
      (match, component) => {
        const sourceRef = match[1].trim();
        return this.handleStyleCopy(sourceRef, component, 'all');
      }
    );

    // Navigation patterns - connect container to a page
    this.commandPatterns.set(
      /(?:set |make |add )?navigation (?:to )?(?:page )?#?["']?([^"']+?)["']?\s*$/i,
      (match) => ({
        properties: {
          action: 'navigate',
          target: match[1].trim()
        }
      })
    );

    this.commandPatterns.set(
      /^navigate to (?:page )?#?["']?([^"']+?)["']?\s*$/i,
      (match) => ({
        properties: {
          action: 'navigate',
          target: match[1].trim()
        }
      })
    );

    this.commandPatterns.set(
      /^(?:link|connect) to (?:page )?#?["']?([^"']+?)["']?\s*$/i,
      (match) => ({
        properties: {
          action: 'navigate',
          target: match[1].trim()
        }
      })
    );

    this.commandPatterns.set(
      /^remove navigation$/i,
      () => ({
        properties: {
          action: undefined,
          target: undefined,
          navigationLabel: undefined
        }
      })
    );
  }

  /**
   * Convert a color string to rgba format with specified opacity
   * Supports hex (#RGB, #RRGGBB), named colors, and existing rgb/rgba
   */
  private colorToRgba(color: string, opacity: number): string {

    // Already rgba - just update opacity
    if (color.startsWith('rgba(')) {
      const match = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/);
      if (match) {
        return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${opacity})`;
      }
    }

    // RGB to RGBA
    if (color.startsWith('rgb(')) {
      const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (match) {
        return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${opacity})`;
      }
    }

    // Hex to RGBA
    if (color.startsWith('#')) {
      let r = 0, g = 0, b = 0;
      if (color.length === 4) {
        // #RGB
        r = parseInt(color[1] + color[1], 16);
        g = parseInt(color[2] + color[2], 16);
        b = parseInt(color[3] + color[3], 16);
      } else if (color.length >= 7) {
        // #RRGGBB or #RRGGBBAA
        r = parseInt(color.slice(1, 3), 16);
        g = parseInt(color.slice(3, 5), 16);
        b = parseInt(color.slice(5, 7), 16);
      }
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    // Named color to RGBA (common colors)
    const namedColorMap: Record<string, [number, number, number]> = {
      'white': [255, 255, 255],
      'black': [0, 0, 0],
      'red': [255, 0, 0],
      'green': [0, 128, 0],
      'blue': [0, 0, 255],
      'yellow': [255, 255, 0],
      'orange': [255, 165, 0],
      'purple': [128, 0, 128],
      'pink': [255, 192, 203],
      'gray': [128, 128, 128],
      'grey': [128, 128, 128],
      'brown': [165, 42, 42],
      'cyan': [0, 255, 255],
      'magenta': [255, 0, 255],
      'transparent': [255, 255, 255],
    };

    const lowerColor = color.toLowerCase();
    if (namedColorMap[lowerColor]) {
      const [r, g, b] = namedColorMap[lowerColor];
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    // Default fallback - white with opacity
    return `rgba(255, 255, 255, ${opacity})`;
  }

  /**
   * Check if the component currently has a gradient set
   * Gets fresh state from the store to handle sequential commands
   */
  private hasGradient(component: Component): boolean {
    try {
      // Get fresh component state from the store
      const freshComponent = useEditorStore.getState().getComponent(component.id);

      if (freshComponent) {
        const freshStyle = this.getFlexStyle(freshComponent);
        const styleBackground = freshStyle?.background;
        if (styleBackground && styleBackground.includes('gradient') && styleBackground !== 'none') {
          return true;
        }
      }
    } catch (e) {
      // Fallback to component if store access fails
    }

    // Also check the passed component (in case store isn't updated yet)
    const styleBackground = this.getFlexStyle(component)?.background;
    if (styleBackground && styleBackground.includes('gradient') && styleBackground !== 'none') {
      return true;
    }

    return false;
  }

  /**
   * Get the current background color from the component
   * Gets fresh state from the store to handle sequential commands
   */
  private getCurrentBackgroundColor(component: Component): string {
    try {
      // Get fresh component state from the store
      const freshComponent = useEditorStore.getState().getComponent(component.id);
      if (freshComponent) {
        // Check properties.backgroundColor first
        const freshProps = this.getFlexProps(freshComponent);
        const propsBg = freshProps?.backgroundColor;
        if (propsBg && propsBg !== 'transparent' && propsBg !== 'none') {
          return propsBg;
        }
        // Check style.backgroundColor as fallback
        const freshStyle = this.getFlexStyle(freshComponent);
        const styleBg = freshStyle?.backgroundColor;
        if (styleBg && styleBg !== 'transparent' && styleBg !== 'none') {
          return styleBg;
        }
      }
    } catch (e) {
      // Fallback to component if store access fails
    }

    // Also check the passed component (in case store isn't updated yet)
    const propsBg = this.getFlexProps(component)?.backgroundColor;
    if (propsBg && propsBg !== 'transparent' && propsBg !== 'none') {
      return propsBg;
    }

    // Default fallback
    return '#ffffff';
  }

  /**
   * Make the component fully opaque by removing transparency
   * Handles both CSS opacity (for gradients) and rgba backgroundColor (for solid colors)
   */
  private makeOpaque(component: Component): PropertyChange {
    const result: PropertyChange = {
      style: { opacity: 1 }  // Always reset CSS opacity
    };

    // Check if backgroundColor is an rgba value with transparency
    try {
      const freshComponent = useEditorStore.getState().getComponent(component.id);
      if (freshComponent) {
        const bgColor = this.getFlexProps(freshComponent)?.backgroundColor;
        if (bgColor && bgColor.startsWith('rgba(')) {
          // Extract RGB values and make fully opaque
          const rgbaMatch = bgColor.match(/rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*[\d.]+\s*\)/);
          if (rgbaMatch) {
            const [, r, g, b] = rgbaMatch;
            const opaqueColor = `rgb(${r}, ${g}, ${b})`;
            result.properties = { backgroundColor: opaqueColor };
          }
        }
      }
    } catch (e) {
      // Fallback - just set opacity
    }

    return result;
  }

  // =========================================================================
  // PHASE 6: Natural Language Helper Methods
  // =========================================================================

  /**
   * Parse a CSS spacing value and convert to pixels
   * Handles px, rem, em, and unitless values
   *
   * @param value - The CSS value to parse (e.g., "16px", "1rem", 16)
   * @param fallback - Default value if parsing fails
   * @returns Value in pixels
   */
  private parseSpacingToPixels(value: string | number | undefined, fallback = 16): number {
    if (value === undefined || value === null) return fallback;
    if (typeof value === 'number') return value;

    const str = String(value).trim();
    const num = parseFloat(str);
    if (isNaN(num)) return fallback;

    // Convert rem to px (assuming 16px base font size)
    if (str.endsWith('rem')) return num * 16;
    // em is context-dependent, assume 16px base
    if (str.endsWith('em')) return num * 16;
    // Already in px or unitless
    return num;
  }

  /**
   * Extracts a spacing-compatible value (string | number | undefined) from an
   * unknown component style field. Never asserts — returns undefined if the
   * value is not string or number.
   */
  private getSpacingValue(val: unknown): string | number | undefined {
    if (typeof val === 'string' || typeof val === 'number') return val;
    return undefined;
  }

  private getCurrentSpacingPixels(
    component: Component,
    style: FlexContainerStyleProps,
    props: FlexContainerProps,
    key: 'padding' | 'gap' | 'margin' | 'borderRadius',
    fallback: number
  ): number {
    const rawStyle = this.typedStyle(component);
    if (rawStyle && Object.hasOwn(rawStyle, key)) {
      return this.parseSpacingToPixels(this.getSpacingValue(rawStyle[key]) ?? 0, fallback);
    }

    const styleValue = style[key];
    if (styleValue !== undefined && styleValue !== null && styleValue !== '') {
      return this.parseSpacingToPixels(this.getSpacingValue(styleValue) ?? 0, fallback);
    }

    const propValue = props[key];
    if (propValue !== undefined && propValue !== null && propValue !== '') {
      return this.parseSpacingToPixels(this.getSpacingValue(propValue) ?? 0, fallback);
    }

    return fallback;
  }

  private adjustContainerDensity(component: Component, increase: boolean): PropertyChange {
    const props = this.getFlexProps(component);
    const style = this.getFlexStyle(component);
    const increment = increase ? 8 : -8;
    const nextGap = Math.max(0, this.getCurrentSpacingPixels(component, style, props, 'gap', 16) + increment);
    const nextPadding = Math.max(0, this.getCurrentSpacingPixels(component, style, props, 'padding', 16) + increment);

    return {
      properties: {
        gap: nextGap,
        padding: nextPadding,
      },
      style: {
        gap: `${nextGap}px`,
        padding: `${nextPadding}px`,
        paddingTop: undefined,
        paddingRight: undefined,
        paddingBottom: undefined,
        paddingLeft: undefined,
      },
    };
  }

  /**
   * Adjust the brightness of a color by a specified amount
   * Positive values make the color lighter, negative values make it darker
   *
   * @param color - The color to adjust (hex, rgb, rgba, or named color)
   * @param amount - Amount to adjust brightness (-100 to 100)
   * @returns Adjusted color as a hex string
   */
  private adjustColorBrightness(color: string, amount: number): string {

    // Parse the color to RGB values
    let r = 255, g = 255, b = 255;

    // Handle hex colors
    if (color.startsWith('#')) {
      if (color.length === 4) {
        // #RGB format
        r = parseInt(color[1] + color[1], 16);
        g = parseInt(color[2] + color[2], 16);
        b = parseInt(color[3] + color[3], 16);
      } else if (color.length >= 7) {
        // #RRGGBB or #RRGGBBAA format
        r = parseInt(color.slice(1, 3), 16);
        g = parseInt(color.slice(3, 5), 16);
        b = parseInt(color.slice(5, 7), 16);
      }
    }
    // Handle rgb/rgba colors
    else if (color.startsWith('rgb')) {
      const match = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
      if (match) {
        r = parseInt(match[1], 10);
        g = parseInt(match[2], 10);
        b = parseInt(match[3], 10);
      }
    }
    // Handle named colors
    else {
      const namedColorMap: Record<string, [number, number, number]> = {
        'white': [255, 255, 255],
        'black': [0, 0, 0],
        'red': [255, 0, 0],
        'green': [0, 128, 0],
        'blue': [0, 0, 255],
        'yellow': [255, 255, 0],
        'orange': [255, 165, 0],
        'purple': [128, 0, 128],
        'pink': [255, 192, 203],
        'gray': [128, 128, 128],
        'grey': [128, 128, 128],
        'brown': [165, 42, 42],
        'cyan': [0, 255, 255],
        'magenta': [255, 0, 255],
        // Brand colors
        'forest green': [75, 99, 59],
        'sage green': [178, 172, 136],
        'seafoam': [147, 233, 190],
        'blue grotto': [17, 107, 152],
        'desert sun': [226, 125, 61],
        'chartreuse': [204, 255, 0],
        'charcoal': [10, 10, 10],
        'champagne': [247, 231, 206],
      };

      const lowerColor = color.toLowerCase();
      if (namedColorMap[lowerColor]) {
        [r, g, b] = namedColorMap[lowerColor];
      }
    }

    // Apply brightness adjustment (clamp between 0 and 255)
    r = Math.max(0, Math.min(255, r + amount));
    g = Math.max(0, Math.min(255, g + amount));
    b = Math.max(0, Math.min(255, b + amount));

    // Convert back to hex
    const toHex = (n: number) => {
      const hex = Math.round(n).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    const result = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    return result;
  }

  /**
   * Adjust the shadow level up or down through presets
   *
   * @param component - The component to adjust
   * @param increase - Whether to increase (true) or decrease (false) the shadow
   * @returns PropertyChange with the new shadow value
   */
  private adjustShadowLevel(component: Component, increase: boolean): PropertyChange {
    const shadowLevels = ['none', 'xs', 'sm', 'md', 'lg', 'xl', '2xl'];
    const style = this.getFlexStyle(component);
    const currentShadow = style?.boxShadow ?? '';

    // Find current level
    let currentLevel = 0; // Default to 'none'

    // Check which preset matches the current shadow
    for (let i = 0; i < shadowLevels.length; i++) {
      const preset = SHADOW_PRESETS[shadowLevels[i]];
      if (currentShadow === preset) {
        currentLevel = i;
        break;
      }
    }

    // If not found, try to estimate based on shadow complexity
    if (currentShadow && currentShadow !== 'none' && currentLevel === 0) {
      // If there's a shadow but we didn't match a preset, assume md
      currentLevel = 3;
    }

    // Adjust level
    const newLevel = increase
      ? Math.min(shadowLevels.length - 1, currentLevel + 1)
      : Math.max(0, currentLevel - 1);

    const newShadow = SHADOW_PRESETS[shadowLevels[newLevel]];


    return { style: { boxShadow: newShadow } };
  }

  /**
   * Adjust the radius level up or down through presets
   *
   * @param component - The component to adjust
   * @param increase - Whether to increase (true) or decrease (false) the radius
   * @returns PropertyChange with the new radius value
   */
  private adjustRadiusLevel(component: Component, increase: boolean): PropertyChange {
    const radiusLevels = ['none', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', 'full'];
    const style = this.getFlexStyle(component);
    const props = this.getFlexProps(component);
    const currentRadius = (typeof style?.borderRadius === 'string' ? style.borderRadius : undefined)
      ?? `${props?.borderRadius ?? 8}px`;

    // Find current level
    let currentLevel = 3; // Default to 'md' (8px)

    // Check which preset matches the current radius
    for (let i = 0; i < radiusLevels.length; i++) {
      const preset = RADIUS_PRESETS[radiusLevels[i]];
      if (currentRadius === preset || currentRadius === preset.replace('px', '')) {
        currentLevel = i;
        break;
      }
    }

    // If numeric radius, estimate level
    const radiusNum = parseInt(currentRadius, 10);
    if (!isNaN(radiusNum)) {
      if (radiusNum === 0) currentLevel = 0;
      else if (radiusNum <= 2) currentLevel = 1;
      else if (radiusNum <= 4) currentLevel = 2;
      else if (radiusNum <= 8) currentLevel = 3;
      else if (radiusNum <= 12) currentLevel = 4;
      else if (radiusNum <= 16) currentLevel = 5;
      else if (radiusNum <= 24) currentLevel = 6;
      else currentLevel = 7;
    }

    // Adjust level
    const newLevel = increase
      ? Math.min(radiusLevels.length - 1, currentLevel + 1)
      : Math.max(0, currentLevel - 1);

    const newRadius = RADIUS_PRESETS[radiusLevels[newLevel]];


    return {
      style: {
        borderRadius: newRadius,
        borderTopLeftRadius: undefined,
        borderTopRightRadius: undefined,
        borderBottomLeftRadius: undefined,
        borderBottomRightRadius: undefined
      }
    };
  }

  // =========================================================================
  // PHASE 7: Style Copying Helper Methods
  // =========================================================================

  /**
   * Map a property type keyword to a StyleCloneScope
   *
   * @param propertyType - The property keyword from the command (e.g., "background", "colors", "border")
   * @returns The corresponding StyleCloneScope
   */
  private mapPropertyToScope(propertyType: string): StyleCloneScope {
    const scopeMap: Record<string, StyleCloneScope> = {
      'background': 'colors',
      'colors': 'colors',
      'color': 'colors',
      'border': 'borders',
      'shadow': 'shadows',
      'spacing': 'spacing',
      'typography': 'typography',
      'font': 'typography',
      'style': 'all',
    };

    return scopeMap[propertyType.toLowerCase()] || 'all';
  }

  /**
   * Handle style copying from a source component
   * Uses StyleCloneService and ComponentReferenceResolver for component lookup
   *
   * @param sourceRef - Reference text for the source component (e.g., "header", "sidebar")
   * @param targetComponent - The target component to apply styles to
   * @param scope - The scope of styles to copy
   * @returns PropertyChange with cloned styles, or null if source not found
   */
  private handleStyleCopy(
    sourceRef: string,
    targetComponent: Component,
    scope: StyleCloneScope
  ): PropertyChange | null {

    // Check for style clone command pattern first
    const cloneInfo = styleCloneService.parseCloneCommand(`copy style from ${sourceRef}`);
    if (cloneInfo) {
    }

    // Try to resolve the source component reference
    const result = componentReferenceResolver.resolveSemanticReference(sourceRef);

    if (!result.success || !result.primaryMatch) {

      // If there are suggestions, we could potentially show them, but for now return null
      if (result.allMatches && result.allMatches.length > 0) {
      }

      return null;
    }

    // Get the source component from the store
    const { components } = useEditorStore.getState();
    const sourceComponent = components.find(c => c.id === result.primaryMatch?.resolvedComponentId);

    if (!sourceComponent) {
      return null;
    }


    // Extract styles from source component
    const extractedStyles = styleCloneService.extractStylesForClone(sourceComponent, scope);

    // Filter for compatibility and build property changes
    const { propertyChanges, warnings } = styleCloneService.filterAndBuildChanges(
      extractedStyles,
      targetComponent,
      scope
    );

    if (warnings.length > 0) {
    }

    if (!propertyChanges) {
      return null;
    }


    // Return the cloned styles (excluding targetComponentId which is set by filterAndBuildChanges)
    const result_changes: PropertyChange = {};
    if (propertyChanges.style && Object.keys(propertyChanges.style).length > 0) {
      result_changes.style = propertyChanges.style;
    }
    if (propertyChanges.properties && Object.keys(propertyChanges.properties).length > 0) {
      result_changes.properties = propertyChanges.properties;
    }
    if (propertyChanges.size) {
      result_changes.size = propertyChanges.size;
    }

    return Object.keys(result_changes).length > 0 ? result_changes : null;
  }

  // =========================================================================
  // PHASE 5: Responsive Styling Helper Methods
  // =========================================================================

  /**
   * Handle a breakpoint-prefixed command
   * Parses the sub-command and wraps the result in responsiveStyles
   *
   * @param breakpointStr - The breakpoint identifier (mobile, tablet, desktop)
   * @param subCommand - The command to apply at that breakpoint
   * @param component - The target component
   * @returns PropertyChange with responsiveStyles or null if sub-command not recognized
   */
  private handleRefreshBadge(component: Component): PropertyChange | null {
    const editorState = useEditorStore.getState();
    const allComponents = editorState.components;

    // Find first TEXT or DATA_TEXT child
    const children = allComponents.filter(c => c.parentId === component.id);
    const textChild = children.find(
      c => c.type === ComponentType.TEXT || c.type === ComponentType.DATA_TEXT,
    );
    if (!textChild) return null;

    // Auto-detect scope: walk up parent chain
    const DATA_TYPES = [
      ComponentType.DATA_TEXT, ComponentType.GAUGE_CHART, ComponentType.VERTICAL_GAUGE_CHART,
      ComponentType.DATA_CARD, ComponentType.LINE_CHART, ComponentType.BAR_CHART,
      ComponentType.PIE_CHART, ComponentType.AREA_CHART, ComponentType.SCATTER_CHART,
      ComponentType.HEATMAP_CHART, ComponentType.TREEMAP_CHART, ComponentType.MAP_CHART,
      ComponentType.TABLE, ComponentType.ADVANCED_TABLE,
    ];
    let scopeContainerId = component.id;
    let candidate = component.parentId;
    while (candidate) {
      const descendants = getComponentDescendants(candidate, allComponents);
      const hasRealData = descendants.some(c => {
        if (!DATA_TYPES.includes(c.type)) return false;
        // Exclude timestamp-aggregate (the badge's own display text)
        const cProps = this.getFlexProps(c);
        return cProps?.dataSource?.type !== 'timestamp-aggregate';
      });
      if (hasRealData) {
        scopeContainerId = candidate;
        break;
      }
      const parent = allComponents.find(c => c.id === candidate);
      candidate = parent?.parentId;
    }

    // Convert text child to DATA_TEXT with timestamp-aggregate
    if (textChild.type === ComponentType.TEXT) {
      const textProps = this.getFlexProps(textChild);
      const content = textProps?.content;
      const { content: _, ...otherProps } = textProps ?? {};
      editorState.updateComponent(textChild.id, {
        type: ComponentType.DATA_TEXT,
        properties: {
          ...otherProps,
          value: content ?? '',
          showLabel: false,
          emptyValueText: String(content || 'Never'),
          dataSource: { type: 'timestamp-aggregate' as const },
          refreshBadgeScopeId: scopeContainerId,
        },
      });
    } else {
      editorState.updateComponent(textChild.id, {
        properties: {
          ...(textChild.properties || {}),
          dataSource: { type: 'timestamp-aggregate' as const },
          refreshBadgeScopeId: scopeContainerId,
        },
      });
    }

    const refreshBadge: RefreshBadgeConfig = {
      enabled: true,
      timestampComponentId: textChild.id,
      scopeContainerId,
      timestampFormat: 'relative',
    };

    return {
      properties: {
        refreshBadge,
      },
    };
  }

  private handleBreakpointCommand(
    breakpointStr: string,
    subCommand: string,
    component: Component
  ): PropertyChange | null {
    const lower = breakpointStr.toLowerCase();
    if (lower !== 'mobile' && lower !== 'tablet' && lower !== 'desktop') return null;
    const breakpoint = lower;
    const trimmedSubCommand = subCommand.trim();


    // Parse sub-command using existing patterns (excluding breakpoint patterns)
    const subResult = this.parseSubCommand(trimmedSubCommand, component);

    if (subResult) {

      // Build responsive styles from the sub-result
      const responsiveStyle: Record<string, unknown> = {};

      // Merge style changes directly
      if (subResult.style) {
        Object.assign(responsiveStyle, subResult.style);
      }

      // Convert properties to styles for breakpoint-specific application
      // These are flex/layout properties that should be applied as CSS at the breakpoint
      if (subResult.properties) {
        // Background color
        if (subResult.properties.backgroundColor) {
          responsiveStyle.backgroundColor = subResult.properties.backgroundColor;
        }
        // Flex direction
        if (subResult.properties.flexDirection) {
          responsiveStyle.flexDirection = subResult.properties.flexDirection;
        }
        // Flex wrap
        if (subResult.properties.flexWrap) {
          responsiveStyle.flexWrap = subResult.properties.flexWrap;
        }
        // Gap - convert number to px string
        if (subResult.properties.gap !== undefined) {
          const gapValue = subResult.properties.gap;
          responsiveStyle.gap = typeof gapValue === 'number' ? `${gapValue}px` : gapValue;
        }
        // Padding - convert number to px string
        if (subResult.properties.padding !== undefined) {
          const paddingValue = subResult.properties.padding;
          responsiveStyle.padding = typeof paddingValue === 'number' ? `${paddingValue}px` : paddingValue;
        }
        // Align items
        if (subResult.properties.alignItems) {
          responsiveStyle.alignItems = subResult.properties.alignItems;
        }
        // Justify content
        if (subResult.properties.justifyContent) {
          responsiveStyle.justifyContent = subResult.properties.justifyContent;
        }
        // Min height
        if (subResult.properties.minHeight !== undefined) {
          responsiveStyle.minHeight = subResult.properties.minHeight;
        }
      }

      // Handle size changes - these should also go to responsive styles
      if (subResult.size) {
        if (subResult.size.width !== undefined) {
          responsiveStyle.width = subResult.size.width;
        }
        if (subResult.size.height !== undefined) {
          responsiveStyle.height = subResult.size.height;
        }
      }


      // Return with responsiveStyles wrapper
      return {
        responsiveStyles: { [breakpoint]: responsiveStyle }
      };
    }

    return null;
  }

  /**
   * Parse a sub-command without matching breakpoint patterns
   * This prevents infinite recursion when parsing breakpoint-prefixed commands
   *
   * @param command - The command to parse
   * @param component - The target component
   * @returns PropertyChange or null if no pattern matched
   */
  private parseSubCommand(command: string, component: Component): PropertyChange | null {
    const normalizedCommand = command.trim().toLowerCase();


    // Check if this looks like a nested breakpoint command (prevent recursion)
    const breakpointPrefixPatterns = [
      /^on\s+(mobile|tablet|desktop)\s+/i,
      /^(mobile|tablet|desktop):\s*/i,
      /^only\s+on\s+(mobile|tablet|desktop)\s+/i,
      /^(mobile|tablet|desktop)\s+(bg|background|padding|margin|gap|border|shadow)/i
    ];

    for (const pattern of breakpointPrefixPatterns) {
      if (pattern.test(normalizedCommand)) {
        return null;
      }
    }

    // Match against existing patterns, skipping breakpoint patterns
    for (const [pattern, handler] of this.commandPatterns) {
      // Skip patterns that contain breakpoint references (our Phase 5 patterns)
      const patternSource = pattern.source;
      if (
        patternSource.includes('mobile|tablet|desktop') ||
        patternSource.includes('on\\s+') && patternSource.includes('mobile')
      ) {
        continue;
      }

      const match = normalizedCommand.match(pattern);
      if (match) {
        const result = handler(match, component);
        if (result) {
          return result;
        }
      }
    }

    return null;
  }

  canHandle(_input: string, component: Component): boolean {
    // Only handle flex-container components
    return component.type === ComponentType.FLEX_CONTAINER ||
           component.type === ComponentType.CONTAINER;
  }

  /**
   * Override matchPatterns to lowercase input (required for Phase 1 patterns)
   * PropertyModificationService calls this method synchronously
   */
  protected override matchPatterns(command: string, component: Component): PropertyChange | null {
    const normalizedCommand = command.trim().toLowerCase();
    for (const [pattern, handler] of this.commandPatterns) {
      const match = normalizedCommand.match(pattern);
      if (match) {
        const result = handler(match, component);
        if (result) {
          // Resolve page name to ID for navigation actions
          if (result.properties?.action === 'navigate' && result.properties?.target) {
            const rawTarget = result.properties?.target;
            if (typeof rawTarget !== 'string') return null;
            const pageTarget = rawTarget;
            const resolution = this.resolvePageTarget(pageTarget);
            if (!resolution.error && resolution.pageId) {
              result.properties.target = resolution.pageId;
              result.properties.navigationLabel = resolution.pageName;
            } else {
              // Can't resolve page synchronously - return null so async path handles it
              return null;
            }
          }

          // Phase 4: Check for uniform margin on full-width container and add warning
          const isUniformMarginCommand = /^(?:set |add )?margin\s+\d+(?:px)?$/i.test(normalizedCommand);
          if (isUniformMarginCommand && this.isFullWidthContainer(component)) {
            const marginValue = result.style?.margin || 'specified';
            result._warnings = [
              `⚠️ This container appears to be full-width. Adding uniform margin (${marginValue}) may cause horizontal overflow.`,
              `💡 Try "padding ${marginValue.toString().replace('px', '')}" for internal spacing`,
              `💡 Try "margin top ${marginValue.toString().replace('px', '')}" for vertical-only margin`,
              `💡 Try "margin horizontal auto" to center with auto margins`
            ];
          }
          return result;
        }
      }
    }
    return null;
  }

  async parseCommand(input: string, component: Component): Promise<CommandResult> {
    if (!this.canHandle(input, component)) {
      return {
        success: false,
        error: 'This handler only works with flex containers'
      };
    }

    const trimmedInput = input.trim().toLowerCase();

    // Try to match patterns
    for (const [pattern, handler] of this.commandPatterns) {
      const match = trimmedInput.match(pattern);
      if (match) {
        const changes = handler(match, component);
        if (changes) {
          // Resolve page name to ID for navigation actions
          if (changes.properties?.action === 'navigate' && changes.properties?.target) {
            const rawTarget = changes.properties?.target;
            if (typeof rawTarget !== 'string') {
              return {
                success: false,
                error: 'Invalid navigation target',
                suggestions: await this.getSuggestions(component)
              };
            }
            const pageTarget = rawTarget;
            const resolution = this.resolvePageTarget(pageTarget);

            if (resolution.error || !resolution.pageId) {
              return {
                success: false,
                error: resolution.error || `Could not find page "${pageTarget}"`,
                suggestions: await this.getSuggestions(component)
              };
            }

            changes.properties.target = resolution.pageId;
            changes.properties.navigationLabel = resolution.pageName;
          }

          const commandType = this.determineCommandType(changes);

          // Phase 4: Check for uniform margin on full-width container
          const isUniformMarginCommand = /^(?:set |add )?margin\s+\d+(?:px)?$/i.test(trimmedInput);
          const suggestions: string[] = [];

          if (isUniformMarginCommand && this.isFullWidthContainer(component)) {
            const marginValue = changes.style?.margin || 'specified';
            suggestions.push(
              `⚠️ This container appears to be full-width. Adding uniform margin (${marginValue}) may cause horizontal overflow.`,
              `💡 Alternative: Use "padding ${marginValue.toString().replace('px', '')}" for internal spacing`,
              `💡 Alternative: Use "margin top ${marginValue.toString().replace('px', '')}" for vertical-only margin`,
              `💡 Alternative: Use "margin horizontal auto" to center with auto margins`
            );
          }

          return {
            success: true,
            command: {
              type: commandType,
              changes,
              confidence: this.calculateConfidence(trimmedInput, changes),
              description: this.generateDescription(changes, component)
            },
            suggestions: suggestions.length > 0 ? suggestions : undefined
          };
        }
      }
    }

    // If no pattern matched, provide suggestions
    return {
      success: false,
      error: `Command not recognized for flex container`,
      suggestions: [
        'Try: "make height full page"',
        'Try: "center everything"',
        'Try: "stack items vertically"',
        'Try: "add gap 20"',
        'Try: "justify content center"'
      ]
    };
  }

  protected override generateDescription(changes: PropertyChange, _component: Component): string {
    const descriptions: string[] = [];

    // Phase 7: Handle preset info request
    if (changes.properties?._presetInfo) {
      return [
        '📦 Available Style Presets',
        '─────────────────────────',
        '',
        'card       → White card with shadow',
        '             padding: 24px | radius: 8px | shadow: md',
        '',
        'panel      → Bordered panel with light background',
        '             padding: 16px | radius: 4px | bg: #f9fafb',
        '',
        'outlined   → Transparent with border only',
        '             radius: 8px | border: 1px solid',
        '',
        'elevated   → Floating card with large shadow',
        '             radius: 12px | shadow: lg | bg: white',
        '',
        '─────────────────────────',
        'Usage: "make it a card" or just "card"'
      ].join('\n');
    }

    if (changes.style?.height === '100vh') {
      descriptions.push('Set container height to full viewport');
    }
    if (changes.properties?.flexDirection) {
      descriptions.push(`Changed layout direction to ${changes.properties.flexDirection}`);
    }
    if (changes.properties?.justifyContent) {
      descriptions.push(`Aligned content: ${changes.properties.justifyContent}`);
    }
    if (changes.properties?.alignItems) {
      descriptions.push(`Aligned items: ${changes.properties.alignItems}`);
    }
    if (changes.properties?.gap !== undefined) {
      descriptions.push(`Set spacing to ${changes.properties.gap}px`);
    }

    // Phase 1: Background color descriptions
    if (changes.properties?.backgroundColor) {
      const bgColor = changes.properties.backgroundColor;
      if (bgColor === 'transparent') {
        descriptions.push('Made background transparent');
      } else if (typeof bgColor === 'string' && bgColor.startsWith('rgba')) {
        descriptions.push(`Set semi-transparent background: ${bgColor}`);
      } else if (typeof bgColor === 'string' && bgColor.startsWith('#')) {
        descriptions.push(`Set background color to ${bgColor}`);
      } else {
        descriptions.push(`Set background to ${bgColor}`);
      }
    }

    // Phase 1: Gradient descriptions
    if (changes.style?.background) {
      const bg = changes.style.background;
      if (typeof bg === 'string' && bg.includes('linear-gradient')) {
        descriptions.push('Applied linear gradient background');
      } else if (typeof bg === 'string' && bg.includes('radial-gradient')) {
        descriptions.push('Applied radial gradient background');
      }
    }

    // Phase 2: Border descriptions
    if (changes.style?.border) {
      if (changes.style.border === 'none') {
        descriptions.push('Removed border');
      } else {
        descriptions.push(`Set border to ${changes.style.border}`);
      }
    }
    if (changes.style?.borderWidth) {
      descriptions.push(`Set border width to ${changes.style.borderWidth}`);
    }
    if (changes.style?.borderStyle) {
      descriptions.push(`Set border style to ${changes.style.borderStyle}`);
    }
    if (changes.style?.borderColor) {
      descriptions.push(`Set border color to ${changes.style.borderColor}`);
    }
    if (changes.style?.borderTop || changes.style?.borderBottom ||
        changes.style?.borderLeft || changes.style?.borderRight) {
      descriptions.push('Applied side-specific border');
    }
    if (changes.style?.borderRadius !== undefined) {
      const radius = changes.style.borderRadius;
      if (radius === '0' || radius === '0px' || radius === 0) {
        descriptions.push('Removed border radius');
      } else if (radius === '9999px' || radius === 9999) {
        descriptions.push('Applied pill shape');
      } else {
        descriptions.push(`Set border radius to ${radius}`);
      }
    }
    if (changes.style?.borderTopLeftRadius || changes.style?.borderTopRightRadius ||
        changes.style?.borderBottomLeftRadius || changes.style?.borderBottomRightRadius) {
      descriptions.push('Applied individual corner radius');
    }

    // Phase 4: Spacing descriptions
    // Padding descriptions
    if (changes.style?.paddingTop !== undefined || changes.style?.paddingBottom !== undefined) {
      if (changes.style?.paddingTop === changes.style?.paddingBottom) {
        descriptions.push(`Set vertical padding to ${changes.style.paddingTop}`);
      } else {
        if (changes.style?.paddingTop !== undefined) {
          descriptions.push(`Set padding-top to ${changes.style.paddingTop}`);
        }
        if (changes.style?.paddingBottom !== undefined) {
          descriptions.push(`Set padding-bottom to ${changes.style.paddingBottom}`);
        }
      }
    }
    if (changes.style?.paddingLeft !== undefined || changes.style?.paddingRight !== undefined) {
      if (changes.style?.paddingLeft === changes.style?.paddingRight) {
        descriptions.push(`Set horizontal padding to ${changes.style.paddingLeft}`);
      } else {
        if (changes.style?.paddingLeft !== undefined) {
          descriptions.push(`Set padding-left to ${changes.style.paddingLeft}`);
        }
        if (changes.style?.paddingRight !== undefined) {
          descriptions.push(`Set padding-right to ${changes.style.paddingRight}`);
        }
      }
    }
    if (changes.properties?.padding !== undefined) {
      descriptions.push(`Set uniform padding to ${changes.properties.padding}px`);
    }

    // Margin descriptions
    if (changes.style?.margin !== undefined) {
      if (changes.style.margin === 'auto') {
        descriptions.push('Centered with auto margins');
      } else if (changes.style.margin === '0px') {
        descriptions.push('Removed all margins');
      } else {
        descriptions.push(`Set uniform margin to ${changes.style.margin}`);
      }
    }
    if (changes.style?.marginTop !== undefined || changes.style?.marginBottom !== undefined) {
      if (changes.style?.marginTop === 'auto' && changes.style?.marginBottom === 'auto') {
        descriptions.push('Centered vertically with auto margins');
      } else if (changes.style?.marginTop === changes.style?.marginBottom) {
        descriptions.push(`Set vertical margin to ${changes.style.marginTop}`);
      }
    }
    if (changes.style?.marginLeft !== undefined || changes.style?.marginRight !== undefined) {
      if (changes.style?.marginLeft === 'auto' && changes.style?.marginRight === 'auto') {
        descriptions.push('Centered horizontally with auto margins');
      } else if (changes.style?.marginLeft === changes.style?.marginRight) {
        descriptions.push(`Set horizontal margin to ${changes.style.marginLeft}`);
      }
    }

    // Row/Column gap descriptions
    if (changes.properties?.rowGap !== undefined) {
      if (changes.properties.rowGap === 0) {
        descriptions.push('Removed row gap');
      } else {
        descriptions.push(`Set row gap to ${changes.properties.rowGap}px`);
      }
    }
    if (changes.properties?.columnGap !== undefined) {
      if (changes.properties.columnGap === 0) {
        descriptions.push('Removed column gap');
      } else {
        descriptions.push(`Set column gap to ${changes.properties.columnGap}px`);
      }
    }

    // Phase 5: Responsive style descriptions
    if (changes.responsiveStyles) {
      const breakpoints = Object.keys(changes.responsiveStyles);
      for (const bp of breakpoints) {
        descriptions.push(`Applied responsive styles for ${bp}`);
      }
    }

    return descriptions.length > 0
      ? descriptions.join(', ')
      : `Applied flex container changes`;
  }

  /**
   * Get example commands for flex-container components
   * Updated with Phase 1-4 enhancements (color, border, shadow, spacing)
   */
  async getExampleCommands(_componentType: ComponentType): Promise<string[]> {
    return [
      // Layout commands
      'make height full page',
      'center everything',
      'stack items vertically',
      'add gap 20',
      'justify content center',
      'set padding 16',
      'make scrollable',
      // Phase 1: Color commands
      'bg forest green',
      'background #ff5500',
      'bg primary',
      'gradient from blue to green',
      'semi-transparent',
      // Phase 2: Border commands
      'add border',
      'border 2px solid red',
      'dashed border',
      'rounded lg',
      'pill',
      // Phase 3: Shadow commands
      'add shadow',
      'shadow lg',
      'sh md',
      'elevation 3',
      'lift',
      'inset shadow',
      'flat',
      // Phase 4: Spacing commands
      'padding vertical 20',
      'padding top 10',
      'padding 10 20',
      'margin 16',
      'margin auto',
      'margin horizontal auto',
      'row gap 12',
      'column gap 8',
      'gap 10 20',
      'pd 16',
      'mg auto',
      // Phase 5: Responsive styling commands
      'on mobile bg red',
      'tablet: padding 16',
      'desktop shadow lg',
      'on mobile direction column',
      // Phase 6: Natural language refinement commands
      'make it darker',
      'lighter background',
      'more padding',
      'less gap',
      'double the padding',
      'half the gap',
      'bigger radius',
      'smaller shadow',
      // Phase 6: Conversational commands
      'can you add shadow?',
      'please add shadow',
      'I want more padding',
      "let's add rounded corners",
      // Phase 7: Advanced features
      'make it a card',
      'panel style',
      'outlined',
      'make it elevated',
      'copy style from header',
      'match sidebar\'s background',
      'same border as the card',
      'look like the header',
    ];
  }

  /**
   * Get contextual suggestions based on component state
   * Updated with Phase 1-4 suggestions (color, border, shadow, spacing)
   */
  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];
    const props = this.getFlexProps(component);
    const style = this.getFlexStyle(component);

    // Layout suggestions
    if (!props.minHeight || props.minHeight !== '100vh') {
      suggestions.push('make height full page');
    }

    if (props.flexDirection === 'row') {
      suggestions.push('stack items vertically');
    } else {
      suggestions.push('stack items horizontally');
    }

    if (props.justifyContent !== 'center' || props.alignItems !== 'center') {
      suggestions.push('center all items');
    }

    if (!props.gap || props.gap === 0) {
      suggestions.push('add spacing between items');
    }

    if (!props.padding || props.padding === 0) {
      suggestions.push('add padding 20');
    }

    // Phase 1: Background color suggestions
    if (props.backgroundColor === 'transparent' || !props.backgroundColor) {
      suggestions.push('bg white');
      suggestions.push('bg forest green');
      suggestions.push('faint background');
    }

    // Phase 1: Gradient suggestions
    if (!style.background || !String(style.background).includes('gradient')) {
      suggestions.push('gradient from blue to green');
    }

    // Phase 1: Opacity suggestions for solid backgrounds
    if (props.backgroundColor &&
        props.backgroundColor !== 'transparent' &&
        !String(props.backgroundColor).includes('rgba')) {
      suggestions.push('bg opacity 50%');
      suggestions.push('semi-transparent');
    }

    // Phase 2: Border suggestions
    if (!style.border && !props.border) {
      suggestions.push('add border');
      suggestions.push('border 2px solid gray');
    }

    // Phase 2: Border radius suggestions
    // Check both props and style for backwards compatibility
    const currentRadius = style.borderRadius || props.borderRadius;
    if (!currentRadius) {
      suggestions.push('rounded lg');
      suggestions.push('rounded corners');
    } else if (currentRadius !== '9999px' && currentRadius !== 9999) {
      suggestions.push('pill');
    }

    // Phase 3: Shadow suggestions
    const currentShadow = style.boxShadow;
    if (!currentShadow || currentShadow === 'none') {
      suggestions.push('add shadow');
      suggestions.push('shadow lg');
      suggestions.push('elevation 2');
    } else {
      suggestions.push('remove shadow');
      suggestions.push('flat');
    }

    // Phase 3: Inset shadow suggestion for cards/containers
    if (!currentShadow || !String(currentShadow).includes('inset')) {
      suggestions.push('inset shadow');
    }

    // Phase 4: Enhanced Spacing suggestions
    // Padding suggestions
    if (!style.paddingTop && !style.paddingBottom) {
      suggestions.push('padding vertical 16');
    }
    if (!style.paddingLeft && !style.paddingRight) {
      suggestions.push('padding horizontal 24');
    }

    // Margin suggestions
    if (!style.margin && !style.marginLeft && !style.marginRight) {
      suggestions.push('margin auto');
      suggestions.push('margin horizontal auto');
    }

    // Row/Column gap suggestions
    if (!props.rowGap && !props.columnGap) {
      suggestions.push('row gap 12');
      suggestions.push('column gap 8');
      suggestions.push('gap 10 20');
    } else if (props.rowGap && !props.columnGap) {
      suggestions.push('column gap 8');
    } else if (!props.rowGap && props.columnGap) {
      suggestions.push('row gap 12');
    }

    // Phase 5: Responsive styling suggestions
    suggestions.push('on mobile padding 8');
    suggestions.push('on tablet direction row');
    suggestions.push('mobile: gap 12');

    // Phase 6: Natural language refinement suggestions
    // Relative value suggestions based on current state
    if (props.backgroundColor && props.backgroundColor !== 'transparent') {
      suggestions.push('make it darker');
      suggestions.push('lighter background');
    }

    // Suggest relative adjustments for existing properties
    if (props.padding && (typeof props.padding === 'number' ? props.padding : 0) > 0) {
      suggestions.push('more padding');
      suggestions.push('double the padding');
    }

    if (props.gap && (typeof props.gap === 'number' ? props.gap : 0) > 0) {
      suggestions.push('less gap');
      suggestions.push('half the gap');
    }

    if (currentShadow && currentShadow !== 'none') {
      suggestions.push('more shadow');
      suggestions.push('less shadow');
    }

    if (currentRadius && currentRadius !== '0' && currentRadius !== '0px') {
      suggestions.push('more rounded');
      suggestions.push('less radius');
    }

    // Phase 7: Preset and style copying suggestions
    // Suggest presets for plain containers
    if (!style.boxShadow || style.boxShadow === 'none') {
      suggestions.push('make it a card');
      suggestions.push('make it elevated');
    }

    if (!style.border && !props.border) {
      suggestions.push('panel style');
      suggestions.push('outlined');
    }

    // Style copying is context-dependent (needs other components)
    suggestions.push('copy style from header');

    return suggestions;
  }
}

// Export singleton instance
export const flexContainerCommandHandler = new FlexContainerCommandHandler();
